import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  MdAdd, MdEdit, MdDelete, MdChevronRight, MdBusiness, 
  MdModelTraining, MdCategory, MdLabel, 
  MdSearch, MdLayers, MdOutlineInventory, MdCheck, MdClose,
  MdUpload, MdDownload, MdStorage,
} from 'react-icons/md';

import {
  erpTableClass,
  erpTableHeadCellClass,
  erpTableHeadRowClass,
  erpTableRowClasses,
  erpTableScrollClass,
  erpTableShellClass,
} from '@euroasiann/shared';
import {
  ClearFiltersButton,
  PageHeader,
  erpPageBtnPrimaryClass,
  erpPageBtnSecondaryClass,
  erpPageTabClasses,
  erpPageTabsClass,
  erpSearchToolbarClass,
  erpSearchToolbarFieldClass,
  erpSearchToolbarInputClass,
  TableBodySkeleton,
} from '@euroasiann/shared/ui';

import { authenticatedFetch } from '../../lib/api';
import { downloadFromResponse } from '../../utils/download';
import { useToast } from '../../components/shared/Toast';
import { Modal } from '../../components/shared/Modal';
import { Pagination } from '../../components/shared/Pagination';
import { CurrencyAmountInput } from '../../components/shared/CurrencyAmountInput';
import { loadStoredCurrency } from '../../lib/currency';
import { useCurrency } from '../../context/CurrencyContext';
import { PartFinderRegistry } from './PartFinderRegistry';

// ─── Interfaces ───

interface PartMediaFile {
  _id?: string;
  fileName: string;
  fileUrl: string;
  fileType?: string;
  imageType?: string;
  docType?: string;
  uploadedAt?: string;
}

interface Part {
  _id: string;
  name: string;
  partNumber: string;
  description?: string;
  priceUSD: number;
  stockQuantity: number;
  msfNumber?: string;
  imoNumber?: string;
  position?: string;
  drawing?: string;
  dimensions?: string;
  images?: PartMediaFile[];
  documents?: PartMediaFile[];
}

interface SubCategory {
  _id: string;
  name: string;
  description?: string;
  parts: Part[];
  brands?: Brand[];
}

interface Model {
  _id: string;
  name: string;
  description?: string;
  subCategories: SubCategory[];
  parts?: Part[];
}

interface Brand {
  _id: string;
  name: string;
  description?: string;
  models: Model[];
}

interface Category {
  _id: string;
  name: string;
  description?: string;
  department?: string;
  brands: Brand[];
  subCategories?: SubCategory[];
  parts?: Part[];
}

type ItemType = 'brand' | 'model' | 'category' | 'subcategory' | 'part';

const LIST_PAGE_SIZE = 10;

type ListMeta = { page: number; total: number; totalPages: number };

const EMPTY_LIST_META: ListMeta = { page: 1, total: 0, totalPages: 1 };

function listPaginationQuery(
  page: number,
  extra: Record<string, string | number | undefined> = {}
) {
  const params = new URLSearchParams();
  params.set('page', String(Math.max(1, page)));
  params.set('limit', String(LIST_PAGE_SIZE));
  for (const [key, value] of Object.entries(extra)) {
    if (value === undefined || value === null || String(value).trim() === '') continue;
    params.set(key, String(value));
  }
  return params.toString();
}

function readListMeta(payload: any, fallbackPage: number): ListMeta {
  const pagination = payload?.pagination || {};
  const total = Number(pagination.total ?? payload?.total ?? 0) || 0;
  const page = Number(pagination.page ?? fallbackPage) || fallbackPage;
  const totalPages = Number(pagination.totalPages) || Math.max(1, Math.ceil(total / LIST_PAGE_SIZE) || 1);
  return { page, total, totalPages };
}

function isAbortError(err: unknown) {
  return (
    (typeof err === 'object' && err !== null && (err as { name?: string }).name === 'AbortError') ||
    (err instanceof DOMException && err.name === 'AbortError')
  );
}

const CATALOG_COLUMN_FILTERS = [
  { key: 'category', label: 'Category', placeholder: 'Any category' },
  { key: 'subcategory', label: 'Subcategory', placeholder: 'Any subcategory' },
  { key: 'brand', label: 'Brand', placeholder: 'Any brand' },
  { key: 'model', label: 'Model', placeholder: 'Any model' },
  { key: 'partNumber', label: 'Part number', placeholder: 'Part number' },
  { key: 'position', label: 'Position', placeholder: 'Position' },
  { key: 'drawing', label: 'Drawing', placeholder: 'Drawing' },
  { key: 'dimensions', label: 'Dimensions', placeholder: 'Dimensions' },
] as const;

/** Subcategory dropdown value when brand is linked directly to a category. */
const BRAND_NO_SUB_CATEGORY = '__none__';

interface EditItem {
  type: ItemType;
  id: string;
  parentId?: string;
  parentType?: ItemType;
  data: any;
}

// ─── Main Component ───

export default function ProductHierarchy() {
  const { showToast } = useToast();
  const { formatAmount } = useCurrency();

  const [categories, setCategories] = useState<Category[]>([]);
  const [allModels, setAllModels] = useState<Array<{ _id: string; name: string; parentBrandName?: string; parentBrandId?: string }>>([]);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debouncedColFilters, setDebouncedColFilters] = useState({
    category: '',
    subcategory: '',
    model: '',
    brand: '',
    partNumber: '',
    msfNumber: '',
    position: '',
    drawing: '',
    dimensions: '',
    description: '',
  });
  
  // Tab control: 'tree' | 'categories' | 'subcategories' | 'brands' | 'models' | 'approvals'
  const [activeDashboardTab, setActiveDashboardTab] = useState<'tree' | 'categories' | 'subcategories' | 'brands' | 'models' | 'approvals' | 'partfinder'>('tree');
  
  // Pending approvals states
  const [pendingBrands, setPendingBrands] = useState<any[]>([]);
  const [pendingModels, setPendingModels] = useState<any[]>([]);
  const [catalogCategories, setCatalogCategories] = useState<
    Array<{ _id: string; name: string; description?: string; department?: string; status?: string }>
  >([]);
  const [catalogCategoriesLoading, setCatalogCategoriesLoading] = useState(false);
  const [categoriesPage, setCategoriesPage] = useState(1);
  const [categoriesMeta, setCategoriesMeta] = useState<ListMeta>(EMPTY_LIST_META);

  const [catalogParts, setCatalogParts] = useState<any[]>([]);
  const [catalogPartsLoading, setCatalogPartsLoading] = useState(false);
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogMeta, setCatalogMeta] = useState<ListMeta>(EMPTY_LIST_META);

  const [tabSubCategories, setTabSubCategories] = useState<any[]>([]);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);
  const [subCategoriesPage, setSubCategoriesPage] = useState(1);
  const [subCategoriesMeta, setSubCategoriesMeta] = useState<ListMeta>(EMPTY_LIST_META);

  const [tabBrands, setTabBrands] = useState<any[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brandsPage, setBrandsPage] = useState(1);
  const [brandsMeta, setBrandsMeta] = useState<ListMeta>(EMPTY_LIST_META);

  const [tabModels, setTabModels] = useState<any[]>([]);
  const [modelsTabLoading, setModelsTabLoading] = useState(false);
  const [modelsPage, setModelsPage] = useState(1);
  const [modelsMeta, setModelsMeta] = useState<ListMeta>(EMPTY_LIST_META);

  const [pendingBrandsPage, setPendingBrandsPage] = useState(1);
  const [pendingModelsPage, setPendingModelsPage] = useState(1);
  const [pendingBrandsMeta, setPendingBrandsMeta] = useState<ListMeta>(EMPTY_LIST_META);
  const [pendingModelsMeta, setPendingModelsMeta] = useState<ListMeta>(EMPTY_LIST_META);
  const [approvalsLoading, setApprovalsLoading] = useState(false);

  const [editItem, setEditItem] = useState<EditItem | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addType, setAddType] = useState<ItemType | null>(null);
  const [addParentId, setAddParentId] = useState<string | null>(null);
  const [addParentType, setAddParentType] = useState<ItemType | null>(null);

  // Column-level filters for the main parts tabular view
  const [colFilters, setColFilters] = useState({
    category: '',
    subcategory: '',
    model: '',
    brand: '',
    partNumber: '',
    msfNumber: '',
    position: '',
    drawing: '',
    dimensions: '',
    description: '',
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    department: 'engine',
    partNumber: '',
    priceUSD: '',
    priceCurrency: loadStoredCurrency(),
    stockQuantity: 0,
    msfNumber: '',
    imoNumber: '',
    position: '',
    drawing: '',
    dimensions: '',
    parentCategoryId: '',
    parentSubCategoryId: '',
    parentBrandId: '',
    parentModelId: '',
    targetParentType: 'category' as 'category' | 'subcategory' | 'brand' | 'model',
  });
  const [partImages, setPartImages] = useState<PartMediaFile[]>([]);
  const [partDocuments, setPartDocuments] = useState<PartMediaFile[]>([]);
  const [partMediaUploading, setPartMediaUploading] = useState(false);
  const partImageInputRef = useRef<HTMLInputElement>(null);
  const partDocInputRef = useRef<HTMLInputElement>(null);

  // Inline part creation states
  const [isAddingPartInline, setIsAddingPartInline] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState<{
    created: number;
    failed: number;
    errors: Array<{ row: number; message: string }>;
  } | null>(null);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);
  const [inlinePartData, setInlinePartData] = useState({
    parentCategoryId: '',
    parentSubCategoryId: '',
    parentBrandId: '',
    parentModelId: '',
    partNumber: '',
    msfNumber: '',
    position: '',
    drawing: '',
    dimensions: '',
    description: '',
    priceUSD: '',
    stockQuantity: '0',
    targetParentType: 'model' as 'model' | 'category',
  });

  const emptyInlinePartData = () => ({
    parentCategoryId: '',
    parentSubCategoryId: '',
    parentBrandId: '',
    parentModelId: '',
    partNumber: '',
    msfNumber: '',
    position: '',
    drawing: '',
    dimensions: '',
    description: '',
    priceUSD: '',
    stockQuantity: '0',
    targetParentType: 'model' as const,
  });

  const catalogSelectCls =
    'w-full min-w-[88px] px-2.5 py-2 bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-lg text-xs font-semibold text-[hsl(var(--foreground))] outline-none transition-all hover:border-[#0088cc]/40 focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/25';
  const catalogInputCls =
    'w-full min-w-[88px] px-2.5 py-2 bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-lg text-xs font-medium text-[hsl(var(--foreground))] outline-none transition-all placeholder:text-[hsl(var(--muted-foreground))]/70 hover:border-[#0088cc]/40 focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/25';
  const inlineAddRowCls =
    'bg-[#0088cc]/10 border-b border-[hsl(var(--border))] shadow-[inset_3px_0_0_0_#0088cc]';

  /** Display-only badge count of populated column filters. */
  const activeColFilterCount = Object.values(colFilters).filter((value) =>
    String(value ?? '').trim() !== ''
  ).length;

  /** Shared presentation for entity list panels (categories / brands / models / approvals). */
  const listStackCls = 'flex flex-col gap-6 animate-in fade-in duration-300';
  const listHeadingCardCls =
    'flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-5 py-4 shadow-sm sm:px-6';
  const listTableCardCls = erpTableShellClass;
  const listPanelTitleCls =
    'inline-flex items-center gap-2 text-sm font-bold text-[hsl(var(--foreground))]';
  const listHeadCellCls = `${erpTableHeadCellClass} px-6 py-3.5`;
  const listCellCls = 'px-6 py-4';
  const listActionBtnCls =
    'rounded-lg p-1.5 text-[hsl(var(--muted-foreground))] transition-all hover:bg-[#0088cc]/10 hover:text-[#38bdf8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0088cc]/60';
  const listDeleteBtnCls =
    'rounded-lg p-1.5 text-red-400 transition-all hover:bg-red-500/15 hover:text-red-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-500/60';
  const listEmptyCls =
    'px-6 py-12 text-center text-sm font-semibold text-[hsl(var(--muted-foreground))]';
  const approveBtnCls =
    'inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/15 px-3 py-1.5 text-xs font-bold text-emerald-300 transition-all hover:bg-emerald-500/25 hover:text-emerald-200';
  const rejectBtnCls =
    'inline-flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/15 px-3 py-1.5 text-xs font-bold text-red-300 transition-all hover:bg-red-500/25 hover:text-red-200';

  const catalogFilterFieldCls = 'flex min-w-0 flex-col gap-1';
  const catalogFilterLabelCls =
    'text-xs font-semibold text-[hsl(var(--muted-foreground))]';
  const catalogFilterInputCls =
    'w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5 text-sm font-medium text-[hsl(var(--foreground))] outline-none transition-all placeholder:text-[hsl(var(--muted-foreground))]/70 hover:border-[#0088cc]/40 focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20';

  const resolveMediaUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const API_URL = (import.meta as any).env?.VITE_API_URL || '';
    return `${API_URL}/${url.replace(/^\/+/, '')}`;
  };

  useEffect(() => {
    const ac = new AbortController();
    void fetchHierarchy(ac.signal);
    void fetchAllModels(ac.signal);
    return () => ac.abort();
  }, []);

  useEffect(() => {
    const next = searchQuery.trim();
    if (next === debouncedSearch) return;
    const timer = setTimeout(() => {
      setDebouncedSearch(next);
      setCatalogPage(1);
      setCategoriesPage(1);
      setSubCategoriesPage(1);
      setBrandsPage(1);
      setModelsPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearch]);

  useEffect(() => {
    const same =
      debouncedColFilters.category === colFilters.category &&
      debouncedColFilters.subcategory === colFilters.subcategory &&
      debouncedColFilters.model === colFilters.model &&
      debouncedColFilters.brand === colFilters.brand &&
      debouncedColFilters.partNumber === colFilters.partNumber &&
      debouncedColFilters.msfNumber === colFilters.msfNumber &&
      debouncedColFilters.position === colFilters.position &&
      debouncedColFilters.drawing === colFilters.drawing &&
      debouncedColFilters.dimensions === colFilters.dimensions &&
      debouncedColFilters.description === colFilters.description;
    if (same) return;
    const timer = setTimeout(() => {
      setDebouncedColFilters(colFilters);
      setCatalogPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [colFilters, debouncedColFilters]);

  useEffect(() => {
    if (activeDashboardTab !== 'approvals') return;
    const ac = new AbortController();
    void fetchPendingApprovals(ac.signal);
    return () => ac.abort();
  }, [activeDashboardTab, pendingBrandsPage, pendingModelsPage]);

  useEffect(() => {
    if (activeDashboardTab !== 'categories') return;
    const ac = new AbortController();
    void fetchCatalogCategories(ac.signal);
    return () => ac.abort();
  }, [activeDashboardTab, categoriesPage, debouncedSearch]);

  useEffect(() => {
    if (activeDashboardTab !== 'tree') return;
    const ac = new AbortController();
    void fetchCatalogParts(ac.signal);
    return () => ac.abort();
  }, [activeDashboardTab, catalogPage, debouncedSearch, debouncedColFilters]);

  useEffect(() => {
    if (activeDashboardTab !== 'subcategories') return;
    const ac = new AbortController();
    void fetchTabSubCategories(ac.signal);
    return () => ac.abort();
  }, [activeDashboardTab, subCategoriesPage, debouncedSearch]);

  useEffect(() => {
    if (activeDashboardTab !== 'brands') return;
    const ac = new AbortController();
    void fetchTabBrands(ac.signal);
    return () => ac.abort();
  }, [activeDashboardTab, brandsPage, debouncedSearch]);

  useEffect(() => {
    if (activeDashboardTab !== 'models') return;
    const ac = new AbortController();
    void fetchTabModels(ac.signal);
    return () => ac.abort();
  }, [activeDashboardTab, modelsPage, debouncedSearch]);

  const fetchCatalogCategories = async (signal?: AbortSignal) => {
    setCatalogCategoriesLoading(true);
    try {
      const qs = listPaginationQuery(categoriesPage, {
        status: 'active',
        skipPopulate: 'true',
        search: debouncedSearch || undefined,
      });
      const res = await authenticatedFetch(`/api/v1/admin/categories?${qs}`, { signal });
      if (signal?.aborted) return;
      if (!res.ok) throw new Error('Failed to load categories');
      const data = await res.json();
      if (signal?.aborted) return;
      setCatalogCategories(Array.isArray(data.data) ? data.data : []);
      setCategoriesMeta(readListMeta(data, categoriesPage));
    } catch (err: any) {
      if (isAbortError(err) || signal?.aborted) return;
      showToast(err.message || 'Failed to load categories', 'error');
    } finally {
      if (!signal?.aborted) setCatalogCategoriesLoading(false);
    }
  };

  const fetchCatalogParts = async (signal?: AbortSignal) => {
    setCatalogPartsLoading(true);
    try {
      const qs = listPaginationQuery(catalogPage, {
        status: 'active',
        search: debouncedSearch || undefined,
        category: debouncedColFilters.category || undefined,
        subcategory: debouncedColFilters.subcategory || undefined,
        brand: debouncedColFilters.brand || undefined,
        model: debouncedColFilters.model || undefined,
        partNumber: debouncedColFilters.partNumber || undefined,
        msfNumber: debouncedColFilters.msfNumber || undefined,
        position: debouncedColFilters.position || undefined,
        drawing: debouncedColFilters.drawing || undefined,
        dimensions: debouncedColFilters.dimensions || undefined,
        description: debouncedColFilters.description || undefined,
      });
      const res = await authenticatedFetch(`/api/v1/admin/product-hierarchy/parts?${qs}`, { signal });
      if (signal?.aborted) return;
      if (!res.ok) throw new Error('Failed to load catalog');
      const data = await res.json();
      if (signal?.aborted) return;
      setCatalogParts(Array.isArray(data.data) ? data.data : []);
      setCatalogMeta(readListMeta(data, catalogPage));
    } catch (err: any) {
      if (isAbortError(err) || signal?.aborted) return;
      showToast(err.message || 'Failed to load catalog', 'error');
      setCatalogParts([]);
    } finally {
      if (!signal?.aborted) setCatalogPartsLoading(false);
    }
  };

  const fetchTabSubCategories = async (signal?: AbortSignal) => {
    setSubCategoriesLoading(true);
    try {
      const qs = listPaginationQuery(subCategoriesPage, {
        status: 'active',
        skipPopulate: 'false',
        search: debouncedSearch || undefined,
      });
      const res = await authenticatedFetch(`/api/v1/admin/product-hierarchy/subcategories?${qs}`, { signal });
      if (signal?.aborted) return;
      if (!res.ok) throw new Error('Failed to load subcategories');
      const data = await res.json();
      if (signal?.aborted) return;
      const rows = Array.isArray(data.data) ? data.data : [];
      setTabSubCategories(
        rows.map((sub: any) => ({
          ...sub,
          parentCategoryName: sub.parentCategoryName || sub.categoryId?.name || '',
          parentCategoryId: sub.parentCategoryId || sub.categoryId?._id || sub.categoryId,
        }))
      );
      setSubCategoriesMeta(readListMeta(data, subCategoriesPage));
    } catch (err: any) {
      if (isAbortError(err) || signal?.aborted) return;
      showToast(err.message || 'Failed to load subcategories', 'error');
      setTabSubCategories([]);
    } finally {
      if (!signal?.aborted) setSubCategoriesLoading(false);
    }
  };

  const fetchTabBrands = async (signal?: AbortSignal) => {
    setBrandsLoading(true);
    try {
      const qs = listPaginationQuery(brandsPage, {
        status: 'active',
        search: debouncedSearch || undefined,
      });
      const res = await authenticatedFetch(`/api/v1/admin/brands?${qs}`, { signal });
      if (signal?.aborted) return;
      if (!res.ok) throw new Error('Failed to load brands');
      const data = await res.json();
      if (signal?.aborted) return;
      const rows = Array.isArray(data.data) ? data.data : [];
      setTabBrands(
        rows.map((brand: any) => ({
          ...brand,
          parentCategoryName: brand.parentCategoryName || brand.categoryId?.name || '',
          parentCategoryId: brand.parentCategoryId || brand.categoryId?._id || brand.categoryId,
          parentSubCategoryName: brand.parentSubCategoryName || brand.subCategoryId?.name || '',
          parentSubCategoryId: brand.parentSubCategoryId || brand.subCategoryId?._id || brand.subCategoryId,
        }))
      );
      setBrandsMeta(readListMeta(data, brandsPage));
    } catch (err: any) {
      if (isAbortError(err) || signal?.aborted) return;
      showToast(err.message || 'Failed to load brands', 'error');
      setTabBrands([]);
    } finally {
      if (!signal?.aborted) setBrandsLoading(false);
    }
  };

  const fetchTabModels = async (signal?: AbortSignal) => {
    setModelsTabLoading(true);
    try {
      const qs = listPaginationQuery(modelsPage, {
        status: 'active',
        search: debouncedSearch || undefined,
      });
      const res = await authenticatedFetch(`/api/v1/admin/models?${qs}`, { signal });
      if (signal?.aborted) return;
      if (!res.ok) throw new Error('Failed to load models');
      const data = await res.json();
      if (signal?.aborted) return;
      const rows = Array.isArray(data.data) ? data.data : [];
      setTabModels(
        rows.map((m: any) => ({
          ...m,
          parentBrandName: m.parentBrandName || m.brandId?.name || '',
          parentBrandId: m.parentBrandId || m.brandId?._id || m.brandId,
        }))
      );
      setModelsMeta(readListMeta(data, modelsPage));
    } catch (err: any) {
      if (isAbortError(err) || signal?.aborted) return;
      showToast(err.message || 'Failed to load models', 'error');
      setTabModels([]);
    } finally {
      if (!signal?.aborted) setModelsTabLoading(false);
    }
  };

  const fetchPendingApprovals = async (signal?: AbortSignal) => {
    setApprovalsLoading(true);
    try {
      const brandQs = listPaginationQuery(pendingBrandsPage, { status: 'pending' });
      const modelQs = listPaginationQuery(pendingModelsPage, { status: 'pending' });
      const [brandRes, modelRes] = await Promise.all([
        authenticatedFetch(`/api/v1/admin/brands?${brandQs}`, { signal }),
        authenticatedFetch(`/api/v1/admin/models?${modelQs}`, { signal }),
      ]);
      if (signal?.aborted) return;
      if (brandRes.ok) {
        const brandData = await brandRes.json();
        if (signal?.aborted) return;
        setPendingBrands(brandData.data || []);
        setPendingBrandsMeta(readListMeta(brandData, pendingBrandsPage));
      }
      if (modelRes.ok) {
        const modelData = await modelRes.json();
        if (signal?.aborted) return;
        setPendingModels(modelData.data || []);
        setPendingModelsMeta(readListMeta(modelData, pendingModelsPage));
      }
    } catch (err: unknown) {
      if (isAbortError(err) || signal?.aborted) return;
      // non-blocking
    } finally {
      if (!signal?.aborted) setApprovalsLoading(false);
    }
  };

  const fetchAllModels = async (signal?: AbortSignal) => {
    try {
      const res = await authenticatedFetch('/api/v1/admin/models?status=active&skipPopulate=true', { signal });
      if (signal?.aborted || !res.ok) return;
      const data = await res.json();
      if (signal?.aborted) return;
      const rows = Array.isArray(data.data) ? data.data : data.data?.data || [];
      setAllModels(
        rows.map((m: any) => ({
          _id: String(m._id),
          name: m.name,
          description: m.description || '',
          parentBrandName: m.brandId?.name || m.brandName || '',
          parentBrandId: m.brandId?._id ? String(m.brandId._id) : m.brandId ? String(m.brandId) : undefined,
        }))
      );
    } catch (err: unknown) {
      if (isAbortError(err) || signal?.aborted) return;
      // non-blocking
    }
  };

  const fetchHierarchy = async (signal?: AbortSignal) => {
    try {
      const res = await authenticatedFetch('/api/v1/admin/product-hierarchy', { signal });
      if (signal?.aborted) return;
      if (!res.ok) throw new Error('Failed to load hierarchy');
      const data = await res.json();
      if (signal?.aborted) return;
      setCategories(data.data || []);

      if (data.data?.length > 0) {
        setExpandedItems(new Set([data.data[0]._id]));
      }
    } catch (err: any) {
      if (isAbortError(err) || signal?.aborted) return;
      showToast(err.message || 'Connection error', 'error');
    }
  };

  const handleApproveBrand = async (id: string) => {
    try {
      const res = await authenticatedFetch(`/api/v1/admin/brands/${id}/approve`, { method: 'POST' });
      if (!res.ok) throw new Error('Approve failed');
      showToast('Brand approved successfully', 'success');
      fetchPendingApprovals();
      fetchHierarchy();
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  };

  const handleRejectBrand = async (id: string) => {
    if (!window.confirm('Reject and delete this brand?')) return;
    try {
      const res = await authenticatedFetch(`/api/v1/admin/brands/${id}/reject`, { method: 'POST' });
      if (!res.ok) throw new Error('Reject failed');
      showToast('Brand rejected', 'success');
      fetchPendingApprovals();
      fetchHierarchy();
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  };

  const handleApproveModel = async (id: string) => {
    try {
      const res = await authenticatedFetch(`/api/v1/admin/models/${id}/approve`, { method: 'POST' });
      if (!res.ok) throw new Error('Approve failed');
      showToast('Model approved successfully', 'success');
      fetchPendingApprovals();
      fetchHierarchy();
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  };

  const handleRejectModel = async (id: string) => {
    if (!window.confirm('Reject and delete this model?')) return;
    try {
      const res = await authenticatedFetch(`/api/v1/admin/models/${id}/reject`, { method: 'POST' });
      if (!res.ok) throw new Error('Reject failed');
      showToast('Model rejected', 'success');
      fetchPendingApprovals();
      fetchHierarchy();
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  };

  // Derive flat data lists for the tables
  const flatData = useMemo(() => {
    const listCategories: Category[] = [];
    const listSubCategories: any[] = [];
    const listBrands: any[] = [];
    const listModels: any[] = [];
    const listParts: any[] = [];

    const matchesSearch = (text?: string) => {
      if (!searchQuery.trim()) return true;
      return text?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false;
    };

    categories.forEach(cat => {
      if (matchesSearch(cat.name) || matchesSearch(cat.description)) {
        listCategories.push(cat);
      }

      cat.subCategories?.forEach(sub => {
        if (matchesSearch(sub.name) || matchesSearch(sub.description)) {
          listSubCategories.push({
            ...sub,
            parentCategoryName: cat.name,
            parentCategoryId: cat._id,
          });
        }

        sub.brands?.forEach(brand => {
          if (matchesSearch(brand.name) || matchesSearch(brand.description)) {
            listBrands.push({
              ...brand,
              parentSubCategoryName: sub.name,
              parentSubCategoryId: sub._id,
              parentCategoryName: cat.name,
              parentCategoryId: cat._id,
            });
          }

          brand.models?.forEach(model => {
            if (matchesSearch(model.name) || matchesSearch(model.description)) {
              listModels.push({
                ...model,
                parentBrandName: brand.name,
                parentBrandId: brand._id,
                parentSubCategoryName: sub.name,
                parentCategoryName: cat.name,
                parentCategoryId: cat._id,
                parentSubCategoryId: sub._id,
              });
            }

            model.parts?.forEach(part => {
              if (matchesSearch(part.name) || matchesSearch(part.partNumber) || matchesSearch(part.description)) {
                listParts.push({
                  ...part,
                  parentModelName: model.name,
                  parentModelId: model._id,
                  parentBrandName: brand.name,
                  parentSubCategoryName: sub.name,
                  parentCategoryName: cat.name,
                });
              }
            });

            model.subCategories?.forEach(subCat => {
              subCat.parts?.forEach(part => {
                if (matchesSearch(part.name) || matchesSearch(part.partNumber) || matchesSearch(part.description)) {
                  listParts.push({
                    ...part,
                    parentModelName: model.name,
                    parentModelId: model._id,
                    parentBrandName: brand.name,
                    parentSubCategoryName: subCat.name,
                    parentSubCategoryId: subCat._id,
                    parentCategoryName: cat.name,
                  });
                }
              });
            });
          });
        });
      });

      cat.brands?.forEach(brand => {
        if (matchesSearch(brand.name) || matchesSearch(brand.description)) {
          listBrands.push({
            ...brand,
            parentCategoryName: cat.name,
            parentCategoryId: cat._id,
          });
        }

        brand.models?.forEach(model => {
          if (matchesSearch(model.name) || matchesSearch(model.description)) {
            listModels.push({
                ...model,
                parentBrandName: brand.name,
                parentBrandId: brand._id,
                parentCategoryName: cat.name,
                parentCategoryId: cat._id,
              });
          }

          model.parts?.forEach(part => {
            if (matchesSearch(part.name) || matchesSearch(part.partNumber) || matchesSearch(part.description)) {
              listParts.push({
                ...part,
                parentModelName: model.name,
                parentModelId: model._id,
                parentBrandName: brand.name,
                parentCategoryName: cat.name,
              });
            }
          });

          model.subCategories?.forEach(subCat => {
            subCat.parts?.forEach(part => {
              if (matchesSearch(part.name) || matchesSearch(part.partNumber) || matchesSearch(part.description)) {
                listParts.push({
                  ...part,
                  parentModelName: model.name,
                  parentModelId: model._id,
                  parentBrandName: brand.name,
                  parentSubCategoryName: subCat.name,
                  parentSubCategoryId: subCat._id,
                  parentCategoryName: cat.name,
                });
              }
            });
          });
        });
      });

      cat.parts?.forEach(part => {
        if (matchesSearch(part.name) || matchesSearch(part.partNumber) || matchesSearch(part.description)) {
          listParts.push({
            ...part,
            parentCategoryName: cat.name,
            parentCategoryId: cat._id,
          });
        }
      });
    });

    return {
      categories: listCategories,
      subCategories: listSubCategories,
      brands: listBrands,
      models: listModels,
      parts: listParts,
    };
  }, [categories, searchQuery]);

  const modelOptions = useMemo(() => {
    const byId = new Map<string, any>();
    flatData.models.forEach((m) => byId.set(m._id, { ...m }));
    allModels.forEach((m) => {
      const existing = byId.get(m._id);
      byId.set(m._id, {
        ...(existing || {}),
        ...m,
        description: m.description ?? existing?.description,
        parentBrandName: m.parentBrandName || existing?.parentBrandName,
        parentBrandId: m.parentBrandId || existing?.parentBrandId,
      });
    });
    return Array.from(byId.values());
  }, [allModels, flatData.models]);

  const hierarchyModels = useMemo(() => {
    const byId = new Map<string, any>();
    flatData.models.forEach((m) => byId.set(m._id, m));
    modelOptions.forEach((m) => {
      const existing = byId.get(m._id);
      byId.set(m._id, existing ? { ...m, ...existing } : m);
    });
    return Array.from(byId.values());
  }, [flatData.models, modelOptions]);

  const inlineSubcategoryOptions = useMemo(() => {
    if (!inlinePartData.parentCategoryId) return flatData.subCategories;
    return flatData.subCategories.filter(
      (s: any) => s.parentCategoryId === inlinePartData.parentCategoryId
    );
  }, [flatData.subCategories, inlinePartData.parentCategoryId]);

  const inlineBrandOptions = useMemo(() => {
    let list = [...flatData.brands] as any[];
    if (inlinePartData.parentCategoryId) {
      list = list.filter(
        (b) => !b.parentCategoryId || b.parentCategoryId === inlinePartData.parentCategoryId
      );
    }
    if (inlinePartData.parentSubCategoryId) {
      list = list.filter((b) => b.parentSubCategoryId === inlinePartData.parentSubCategoryId);
    }
    const seen = new Set<string>();
    return list.filter((b) => {
      if (seen.has(b._id)) return false;
      seen.add(b._id);
      return true;
    });
  }, [flatData.brands, inlinePartData.parentCategoryId, inlinePartData.parentSubCategoryId]);

  const brandFormSubCategoryOptions = useMemo(() => {
    if (!formData.parentCategoryId) return [];
    return flatData.subCategories.filter(
      (sub: { parentCategoryId?: string }) => sub.parentCategoryId === formData.parentCategoryId
    );
  }, [flatData.subCategories, formData.parentCategoryId]);

  const inlineModelOptions = useMemo(() => {
    if (!inlinePartData.parentBrandId) return hierarchyModels;
    return hierarchyModels.filter((m) => m.parentBrandId === inlinePartData.parentBrandId);
  }, [hierarchyModels, inlinePartData.parentBrandId]);

  const applyInlineModelSelection = (modelId: string) => {
    const modelObj = hierarchyModels.find((m) => m._id === modelId);
    const brandObj = flatData.brands.find((b: any) => b._id === modelObj?.parentBrandId);
    setInlinePartData((prev) => ({
      ...prev,
      parentModelId: modelId,
      parentBrandId: modelObj?.parentBrandId || prev.parentBrandId,
      parentCategoryId: brandObj?.parentCategoryId || modelObj?.parentCategoryId || prev.parentCategoryId,
      parentSubCategoryId: brandObj?.parentSubCategoryId || prev.parentSubCategoryId,
      targetParentType: 'model',
    }));
  };

  const renderStockBadge = (qty: number | undefined) => {
    const q = qty ?? 0;
    const tone =
      q > 5
        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
        : q > 0
          ? 'bg-amber-500/15 text-amber-200 border-amber-500/30'
          : 'bg-red-500/15 text-red-300 border-red-500/30';
    return (
      <span
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-bold tracking-wide ${tone}`}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
        {q} in stock
      </span>
    );
  };

  const renderInlinePartAddRow = (cellPad: string) => (
    <tr className={inlineAddRowCls}>
      <td className={cellPad}>
        <select
          className={catalogSelectCls}
          value={inlinePartData.parentCategoryId}
          onChange={(e) =>
            setInlinePartData({
              ...inlinePartData,
              parentCategoryId: e.target.value,
              parentSubCategoryId: '',
              parentBrandId: '',
              parentModelId: '',
            })
          }
        >
          <option value="">Category...</option>
          {flatData.categories.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name}
            </option>
          ))}
        </select>
      </td>
      <td className={cellPad}>
        <select
          className={catalogSelectCls}
          value={inlinePartData.parentSubCategoryId}
          onChange={(e) =>
            setInlinePartData({
              ...inlinePartData,
              parentSubCategoryId: e.target.value,
              parentBrandId: '',
              parentModelId: '',
            })
          }
          disabled={!inlinePartData.parentCategoryId && inlineSubcategoryOptions.length === 0}
        >
          <option value="">Subcategory...</option>
          {inlineSubcategoryOptions.map((s: any) => (
            <option key={s._id} value={s._id}>
              {s.name}
            </option>
          ))}
        </select>
      </td>
      <td className={cellPad}>
        <select
          className={catalogSelectCls}
          value={inlinePartData.parentBrandId}
          onChange={(e) =>
            setInlinePartData({
              ...inlinePartData,
              parentBrandId: e.target.value,
              parentModelId: '',
            })
          }
        >
          <option value="">Brand...</option>
          {inlineBrandOptions.map((b: any) => (
            <option key={b._id} value={b._id}>
              {b.name}
            </option>
          ))}
        </select>
      </td>
      <td className={cellPad}>
        <select
          className={catalogSelectCls}
          value={inlinePartData.parentModelId}
          onChange={(e) => applyInlineModelSelection(e.target.value)}
        >
          <option value="">Model...</option>
          {(inlinePartData.parentBrandId ? inlineModelOptions : hierarchyModels).map((m) => (
            <option key={m._id} value={m._id}>
              {m.name}
            </option>
          ))}
        </select>
      </td>
      <td className={cellPad}>
        <input
          type="text"
          placeholder="Part Number"
          className={catalogInputCls}
          value={inlinePartData.partNumber}
          onChange={(e) => setInlinePartData({ ...inlinePartData, partNumber: e.target.value })}
        />
      </td>
      <td className={cellPad}>
        <input
          type="text"
          placeholder="Position Number"
          className={catalogInputCls}
          value={inlinePartData.position}
          onChange={(e) => setInlinePartData({ ...inlinePartData, position: e.target.value })}
        />
      </td>
      <td className={cellPad}>
        <input
          type="text"
          placeholder="Drawing Number"
          className={catalogInputCls}
          value={inlinePartData.drawing}
          onChange={(e) => setInlinePartData({ ...inlinePartData, drawing: e.target.value })}
        />
      </td>
      <td className={cellPad}>
        <input
          type="text"
          placeholder="Dimensions"
          className={catalogInputCls}
          value={inlinePartData.dimensions}
          onChange={(e) => setInlinePartData({ ...inlinePartData, dimensions: e.target.value })}
        />
      </td>
      <td className={cellPad}>
        <input
          type="text"
          placeholder="Price"
          className={catalogInputCls}
          value={inlinePartData.priceUSD}
          onChange={(e) => setInlinePartData({ ...inlinePartData, priceUSD: e.target.value })}
        />
      </td>
      <td className={cellPad}>
        <input
          type="number"
          placeholder="Stock"
          className={catalogInputCls}
          value={inlinePartData.stockQuantity}
          onChange={(e) => setInlinePartData({ ...inlinePartData, stockQuantity: e.target.value })}
        />
      </td>
      <td className={`${cellPad} text-right`}>
        <div className="flex justify-end gap-1">
          <button
            onClick={handleSaveInlinePart}
            className="p-1.5 text-emerald-400 hover:bg-emerald-500/15 rounded-lg"
            title="Save"
            type="button"
          >
            <MdCheck className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIsAddingPartInline(false)}
            className="p-1.5 text-red-400 hover:bg-red-500/15 rounded-lg"
            title="Cancel"
            type="button"
          >
            <MdClose className="w-5 h-5" />
          </button>
        </div>
      </td>
    </tr>
  );

  const renderCatalogPartRow = (part: any, cellPad: string) => (
    <tr key={part._id} className={`${erpTableRowClasses()} group text-sm text-[hsl(var(--foreground))]`}>
      <td className={`${cellPad} font-semibold`}>{part.parentCategoryName || '—'}</td>
      <td className={`${cellPad} text-[hsl(var(--muted-foreground))]`}>{part.parentSubCategoryName || '—'}</td>
      <td className={`${cellPad} font-bold`}>{part.parentBrandName || '—'}</td>
      <td className={`${cellPad} text-[hsl(var(--muted-foreground))]`}>{part.parentModelName || '—'}</td>
      <td
        onClick={() => openPartEdit(part)}
        className={`${cellPad} font-bold whitespace-nowrap cursor-pointer text-[#38bdf8] underline-offset-4 transition-colors hover:text-[#7dd3fc] hover:underline`}
      >
        {part.partNumber || '—'}
      </td>
      <td className={`${cellPad} text-[hsl(var(--muted-foreground))]`}>{part.position || '—'}</td>
      <td className={`${cellPad} text-[hsl(var(--muted-foreground))]`}>{part.drawing || '—'}</td>
      <td className={`${cellPad} text-[hsl(var(--muted-foreground))]`}>{part.dimensions || '—'}</td>
      <td className={`${cellPad} font-extrabold tabular-nums whitespace-nowrap`}>{formatAmount(part.priceUSD || 0)}</td>
      <td className={cellPad}>{renderStockBadge(part.stockQuantity)}</td>
      <td className={`${cellPad} text-right`}>
        <div className="flex justify-end gap-1.5 items-center">
          {(part.stockQuantity ?? 0) > 0 ? (
            <span
              className="mr-1 hidden whitespace-nowrap rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-300 xl:inline-flex"
              title="Listed in Admin Inventory (in stock)"
            >
              In inventory
            </span>
          ) : null}
          <button
            type="button"
            aria-label="Edit part"
            onClick={(e) => onEditClick(e, 'part', part, part.parentModelId || part.modelId || part.parentCategoryId || part.categoryId, part.parentModelId || part.modelId ? 'model' : 'category')}
            className="rounded-lg p-1.5 text-[hsl(var(--muted-foreground))] transition-all hover:bg-[#0088cc]/10 hover:text-[#38bdf8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0088cc]/60"
          >
            <MdEdit className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label="Delete part"
            onClick={(e) => onDeleteClick(e, 'part', part)}
            className="rounded-lg p-1.5 text-red-400 transition-all hover:bg-red-500/15 hover:text-red-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-500/60"
          >
            <MdDelete className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  const catalogTableHead = (cellPad: string) => {
    const headCell = `${cellPad} sticky top-0 z-20 bg-[#0088cc]/15 text-[11px] font-bold uppercase tracking-[0.08em] text-[#38bdf8] whitespace-nowrap backdrop-blur-md`;
    return (
      <tr className="border-b border-[hsl(var(--border))]">
        <th className={headCell}>Category</th>
        <th className={headCell}>Subcategory</th>
        <th className={headCell}>Brand</th>
        <th className={headCell}>Model</th>
        <th className={headCell}>Part Number</th>
        <th className={headCell}>Position Number</th>
        <th className={headCell}>Drawing Number</th>
        <th className={headCell}>Dimensions</th>
        <th className={headCell}>Price</th>
        <th className={headCell}>Stock</th>
        <th className={`${headCell} text-right`}>Actions</th>
      </tr>
    );
  };

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openAddModal = (type: ItemType, parentId?: string, parentType?: ItemType) => {
    setAddType(type);
    setAddParentId(parentId || null);
    setAddParentType(parentType || null);

    let parentCategoryId = parentId && parentType === 'category' ? parentId : '';
    let parentSubCategoryId = parentId && parentType === 'subcategory' ? parentId : '';
    if (type === 'brand' && parentType === 'subcategory' && parentId) {
      for (const cat of categories) {
        const match = cat.subCategories?.find((s) => s._id === parentId);
        if (match) {
          parentCategoryId = cat._id;
          break;
        }
      }
    }

    setFormData({
      name: '',
      description: '',
      department: 'engine',
      partNumber: '',
      priceUSD: '',
      priceCurrency: loadStoredCurrency(),
      stockQuantity: 0,
      msfNumber: '',
      imoNumber: '',
      position: '',
      drawing: '',
      dimensions: '',
      parentCategoryId,
      parentSubCategoryId,
      parentBrandId: parentId && (parentType === 'brand') ? parentId : '',
      parentModelId: parentId && (parentType === 'model') ? parentId : '',
      targetParentType: (parentType || 'category') as any,
    });
    setPartImages([]);
    setPartDocuments([]);
    setIsAddModalOpen(true);
  };

  const resolveItemId = (item: { _id?: unknown; id?: unknown }) => {
    const raw = item._id ?? item.id;
    if (raw == null || raw === '') return '';
    if (typeof raw === 'object' && raw !== null && 'toString' in raw) {
      return String((raw as { toString(): string }).toString());
    }
    const asString = String(raw);
    return asString === 'undefined' ? '' : asString;
  };

  const parseApiError = async (res: Response, fallback: string) => {
    try {
      const json = await res.json();
      return json.error || json.message || fallback;
    } catch {
      return fallback;
    }
  };

  const openEditModal = (type: ItemType, item: any, parentId?: string, parentType?: ItemType) => {
    setIsAddModalOpen(false);
    setAddType(null);
    setAddParentId(null);
    setAddParentType(null);

    const itemId = resolveItemId(item);
    if (!itemId) {
      showToast('Cannot edit this item (missing id)', 'error');
      return;
    }
    setEditItem({ type, id: itemId, parentId, parentType, data: item });

    let parentCategoryId = '';
    let parentSubCategoryId = '';
    let parentBrandId = '';
    let parentModelId = '';

    if (type === 'brand') {
      parentCategoryId =
        item.parentCategoryId || (parentType === 'category' ? parentId || '' : '');
      if (item.parentSubCategoryId) {
        parentSubCategoryId = item.parentSubCategoryId;
      } else if (parentType === 'subcategory' && parentId) {
        parentSubCategoryId = parentId;
      } else {
        parentSubCategoryId = BRAND_NO_SUB_CATEGORY;
      }
    } else if (type === 'subcategory') {
      parentCategoryId = item.parentCategoryId || parentId || '';
    } else if (type === 'model') {
      parentBrandId = item.parentBrandId || parentId || '';
    } else if (type === 'part') {
      parentModelId =
        item.parentModelId ||
        item.modelId ||
        (parentType === 'model' ? parentId || '' : '');
      parentCategoryId =
        item.parentCategoryId ||
        item.categoryId ||
        (parentType === 'category' ? parentId || '' : '');
    }

    setFormData({
      name: item.name || item.partNumber || '',
      description: item.description || '',
      department: item.department || 'engine',
      partNumber: (type === 'part' ? item.partNumber : '') || '',
      priceUSD: type === 'part' ? String(item.priceUSD ?? '') : '',
      priceCurrency: loadStoredCurrency(),
      stockQuantity: type === 'part' ? Number(item.stockQuantity) || 0 : 0,
      msfNumber: (type === 'part' ? item.msfNumber : '') || '',
      imoNumber: (type === 'part' ? item.imoNumber : '') || '',
      position: (type === 'part' ? item.position : '') || '',
      drawing: (type === 'part' ? item.drawing : '') || '',
      dimensions: (type === 'part' ? item.dimensions : '') || '',
      parentCategoryId,
      parentSubCategoryId,
      parentBrandId,
      parentModelId,
      targetParentType: (parentType || (type === 'part' && parentModelId ? 'model' : 'category')) as any,
    });
    setPartImages(type === 'part' ? item.images || [] : []);
    setPartDocuments(type === 'part' ? item.documents || [] : []);
  };

  const onEditClick = (
    e: React.MouseEvent,
    type: ItemType,
    item: any,
    parentId?: string,
    parentType?: ItemType
  ) => {
    e.preventDefault();
    e.stopPropagation();
    openEditModal(type, item, parentId, parentType);
  };

  const onDeleteClick = (e: React.MouseEvent, type: ItemType, item: any) => {
    e.preventDefault();
    e.stopPropagation();
    const id = resolveItemId(item);
    const label = item.name || item.partNumber || 'this item';
    void handleDelete(type, id, label);
  };

  const openPartEdit = (part: any) => {
    const parentId =
      part.parentModelId ||
      part.modelId ||
      part.parentCategoryId ||
      part.categoryId;
    const parentType: ItemType =
      part.parentModelId || part.modelId ? 'model' : 'category';
    openEditModal('part', part, parentId, parentType);
  };

  const handleSave = async () => {
    try {
      const primaryName = formData.name.trim();
      if (!primaryName) {
        showToast('Primary name is required', 'error');
        return;
      }

      let url = '';
      let method: 'POST' | 'PUT' = 'POST';
      const isEdit = !!editItem;
      const type = editItem?.type || addType!;
      if (isEdit && !editItem!.id) {
        showToast('Cannot save: missing item id', 'error');
        return;
      }
      let body: any = { name: primaryName, description: formData.description?.trim() };

      if (type === 'brand') {
        if (!formData.parentCategoryId) {
          showToast('Category is required', 'error');
          return;
        }
        if (!formData.parentSubCategoryId) {
          showToast('Sub category is required — select one or choose No sub category', 'error');
          return;
        }
        url = isEdit ? `/api/v1/admin/brands/${editItem!.id}` : '/api/v1/admin/brands';
        method = isEdit ? 'PUT' : 'POST';
        body.categoryId = formData.parentCategoryId;
        if (formData.parentSubCategoryId === BRAND_NO_SUB_CATEGORY) {
          body.subCategoryId = null;
        } else {
          body.subCategoryId = formData.parentSubCategoryId;
        }
      } else if (type === 'model') {
        url = isEdit ? `/api/v1/admin/models/${editItem!.id}` : '/api/v1/admin/models';
        method = isEdit ? 'PUT' : 'POST';
        const brandId = isEdit
          ? formData.parentBrandId || editItem!.parentId || editItem!.data?.parentBrandId
          : addParentId || formData.parentBrandId;
        if (!isEdit && !brandId) {
          showToast('Parent brand is required', 'error');
          return;
        }
        if (brandId) body.brandId = brandId;
      } else if (type === 'category') {
        url = isEdit ? `/api/v1/admin/categories/${editItem!.id}` : '/api/v1/admin/categories';
        method = isEdit ? 'PUT' : 'POST';
        body.department = formData.department;
      } else if (type === 'subcategory') {
        url = isEdit
          ? `/api/v1/admin/product-hierarchy/subcategories/${editItem!.id}`
          : '/api/v1/admin/product-hierarchy/subcategories';
        method = isEdit ? 'PUT' : 'POST';
        if (isEdit) {
          body = { name: primaryName, description: formData.description?.trim() };
        } else {
          const pType = (addParentType || 'category') as ItemType;
          const pId = addParentId || formData.parentCategoryId;
          if (!pId) {
            showToast('Parent category is required', 'error');
            return;
          }
          if (pType === 'category') {
            body.categoryId = pId;
            body.modelId = null;
          } else {
            body.modelId = pId;
          }
        }
      } else if (type === 'part') {
        url = isEdit ? `/api/v1/admin/product-hierarchy/parts/${editItem!.id}` : '/api/v1/admin/product-hierarchy/parts';
        method = isEdit ? 'PUT' : 'POST';
        body = {
          ...body,
          partNumber: formData.partNumber.trim(),
          priceUSD: Number(formData.priceUSD),
          stockQuantity: Number(formData.stockQuantity),
          msfNumber: formData.msfNumber.trim(),
          imoNumber: formData.imoNumber.trim(),
          position: formData.position.trim(),
          drawing: formData.drawing.trim(),
          dimensions: formData.dimensions.trim(),
        };
        if (!isEdit) {
          const pType = (addParentType || (formData.parentModelId ? 'model' : 'category')) as any;
          const pId = addParentId || (pType === 'model' ? formData.parentModelId : formData.parentCategoryId);
          if (pType === 'subcategory') {
            body.subCategoryId = pId;
          } else if (pType === 'model') {
            body.modelId = pId;
          } else if (pType === 'category') {
            body.categoryId = pId;
          }
        }
      }

      const res = await authenticatedFetch(url, { method, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(await parseApiError(res, 'Save operation failed'));

      showToast(`Item ${isEdit ? 'updated' : 'created'}`, 'success');
      setEditItem(null);
      setIsAddModalOpen(false);
      fetchHierarchy();
      if (type === 'category') void fetchCatalogCategories();
      if (type === 'subcategory') void fetchTabSubCategories();
      if (type === 'brand') void fetchTabBrands();
      if (type === 'model') {
        void fetchAllModels();
        void fetchTabModels();
      }
      if (type === 'part') void fetchCatalogParts();
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  };

  const handleSaveInlinePart = async () => {
    try {
      if (!inlinePartData.partNumber.trim()) {
        showToast('Part number is required', 'error');
        return;
      }
      if (!inlinePartData.parentBrandId) {
        showToast('Please select a brand', 'error');
        return;
      }
      if (!inlinePartData.parentModelId) {
        showToast('Please select a model', 'error');
        return;
      }

      const body = {
        name: inlinePartData.description.trim() || `Part ${inlinePartData.partNumber.trim()}`,
        partNumber: inlinePartData.partNumber.trim(),
        description: inlinePartData.description.trim(),
        priceUSD: Number(inlinePartData.priceUSD) || 0,
        stockQuantity: Number(inlinePartData.stockQuantity) || 0,
        msfNumber: inlinePartData.msfNumber.trim(),
        position: inlinePartData.position.trim(),
        drawing: inlinePartData.drawing.trim(),
        dimensions: inlinePartData.dimensions.trim(),
        modelId: inlinePartData.parentModelId,
        status: 'active',
      };

      const res = await authenticatedFetch('/api/v1/admin/product-hierarchy/parts', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('Save operation failed');

      showToast('Part created successfully', 'success');
      setIsAddingPartInline(false);
      fetchHierarchy();
      void fetchCatalogParts();
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  };

  const handleDownloadPartsTemplate = async () => {
    try {
      const res = await authenticatedFetch('/api/v1/admin/product-hierarchy/parts/bulk-template');
      if (!res.ok) throw new Error('Failed to download template');
      await downloadFromResponse(res, 'catalog-parts-bulk-template.xlsx', '.xlsx');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Download failed';
      showToast(message, 'error');
    }
  };

  const handleBulkPartsFile = async (file: File) => {
    setBulkUploading(true);
    setBulkUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await authenticatedFetch('/api/v1/admin/product-hierarchy/parts/bulk-upload', {
        method: 'POST',
        body: formData,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Bulk upload failed');
      setBulkUploadResult(json.data);
      showToast(json.message || 'Bulk upload complete', 'success');
      fetchHierarchy();
      fetchCatalogParts();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Bulk upload failed';
      showToast(message, 'error');
    } finally {
      setBulkUploading(false);
      if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
    }
  };

  const syncPartMediaFromResponse = (part: any) => {
    if (!part) return;
    setPartImages(part.images || []);
    setPartDocuments(part.documents || []);
  };

  const handleUploadPartImages = async (files: FileList | null) => {
    if (!editItem || editItem.type !== 'part' || !files?.length) return;
    setPartMediaUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append('images', f));
      const res = await authenticatedFetch(
        `/api/v1/admin/product-hierarchy/parts/${editItem.id}/images`,
        { method: 'POST', body: fd }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Image upload failed');
      syncPartMediaFromResponse(json.data);
      showToast(json.message || 'Images uploaded', 'success');
      fetchHierarchy();
      fetchCatalogParts();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Image upload failed', 'error');
    } finally {
      setPartMediaUploading(false);
      if (partImageInputRef.current) partImageInputRef.current.value = '';
    }
  };

  const handleUploadPartDocuments = async (files: FileList | null, docType?: string) => {
    if (!editItem || editItem.type !== 'part' || !files?.length) return;
    setPartMediaUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append('documents', f));
      if (docType) fd.append('docType', docType);
      const res = await authenticatedFetch(
        `/api/v1/admin/product-hierarchy/parts/${editItem.id}/documents`,
        { method: 'POST', body: fd }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Document upload failed');
      syncPartMediaFromResponse(json.data);
      showToast(json.message || 'Documents uploaded', 'success');
      fetchHierarchy();
      fetchCatalogParts();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Document upload failed', 'error');
    } finally {
      setPartMediaUploading(false);
      if (partDocInputRef.current) partDocInputRef.current.value = '';
    }
  };

  const handleDeletePartImage = async (imageId?: string) => {
    if (!editItem || editItem.type !== 'part' || !imageId) return;
    try {
      const res = await authenticatedFetch(
        `/api/v1/admin/product-hierarchy/parts/${editItem.id}/images/${imageId}`,
        { method: 'DELETE' }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to remove image');
      syncPartMediaFromResponse(json.data);
      showToast('Image removed', 'success');
      fetchHierarchy();
      fetchCatalogParts();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to remove image', 'error');
    }
  };

  const handleDeletePartDocument = async (documentId?: string) => {
    if (!editItem || editItem.type !== 'part' || !documentId) return;
    try {
      const res = await authenticatedFetch(
        `/api/v1/admin/product-hierarchy/parts/${editItem.id}/documents/${documentId}`,
        { method: 'DELETE' }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to remove document');
      syncPartMediaFromResponse(json.data);
      showToast('Document removed', 'success');
      fetchHierarchy();
      fetchCatalogParts();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to remove document', 'error');
    }
  };

  const handleDelete = async (type: ItemType, id: string, name: string) => {
    if (!window.confirm(`Delete ${name || 'this item'}?`)) return;
    const itemId = String(id);
    if (!itemId || itemId === 'undefined') {
      showToast('Invalid item id', 'error');
      return;
    }
    try {
      let url = '';
      if (type === 'brand') url = `/api/v1/admin/brands/${itemId}`;
      else if (type === 'model') url = `/api/v1/admin/models/${itemId}`;
      else if (type === 'category') url = `/api/v1/admin/categories/${itemId}`;
      else if (type === 'subcategory') url = `/api/v1/admin/product-hierarchy/subcategories/${itemId}`;
      else if (type === 'part') url = `/api/v1/admin/product-hierarchy/parts/${itemId}`;

      const res = await authenticatedFetch(url, { method: 'DELETE' });
      if (!res.ok) throw new Error(await parseApiError(res, 'Delete failed'));
      showToast('Successfully deleted', 'success');
      fetchHierarchy();
      if (type === 'category') void fetchCatalogCategories();
      if (type === 'subcategory') void fetchTabSubCategories();
      if (type === 'brand') void fetchTabBrands();
      if (type === 'part') void fetchCatalogParts();
      if (type === 'model') {
        void fetchAllModels();
        void fetchTabModels();
      }
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  };

  // Filter hierarchy based on search query
  const filterHierarchy = (categoriesList: Category[], query: string): Category[] => {
    if (!query.trim()) return categoriesList;
    
    const lowerQuery = query.toLowerCase();
    
    return categoriesList
      .map((category) => {
        const categoryMatches = 
          category.name.toLowerCase().includes(lowerQuery) ||
          category.description?.toLowerCase().includes(lowerQuery);
        
        const filteredBrands = category.brands
          ?.map((brand) => {
            const brandMatches =
              brand.name.toLowerCase().includes(lowerQuery) ||
              brand.description?.toLowerCase().includes(lowerQuery);
            
            const filteredModels = brand.models
              ?.map((model) => {
                const modelMatches =
                  model.name.toLowerCase().includes(lowerQuery) ||
                  model.description?.toLowerCase().includes(lowerQuery);
                
                const filteredSubCategories = model.subCategories
                  ?.map((subCategory) => {
                    const subCategoryMatches =
                      subCategory.name.toLowerCase().includes(lowerQuery) ||
                      subCategory.description?.toLowerCase().includes(lowerQuery);
                    
                    const filteredParts = subCategory.parts?.filter(
                      (part) =>
                        part.name.toLowerCase().includes(lowerQuery) ||
                        part.partNumber.toLowerCase().includes(lowerQuery) ||
                        part.description?.toLowerCase().includes(lowerQuery)
                    );
                    
                    if (subCategoryMatches || (filteredParts && filteredParts.length > 0)) {
                      return { ...subCategory, parts: filteredParts || [] };
                    }
                    return null;
                  })
                  .filter(Boolean) as SubCategory[];
                
                if (modelMatches || (filteredSubCategories && filteredSubCategories.length > 0)) {
                  return { ...model, subCategories: filteredSubCategories || [] };
                }
                return null;
              })
              .filter(Boolean) as Model[];
            
            if (brandMatches || (filteredModels && filteredModels.length > 0)) {
              return { ...brand, models: filteredModels || [] };
            }
            return null;
          })
          .filter(Boolean) as Brand[];
        
        if (categoryMatches || (filteredBrands && filteredBrands.length > 0)) {
          return { ...category, brands: filteredBrands || [] };
        }
        return null;
      })
      .filter(Boolean) as Category[];
  };

  const filteredCategories = useMemo(() => {
    return filterHierarchy(categories, searchQuery);
  }, [categories, searchQuery]);

  const searchPlaceholder =
    activeDashboardTab === 'tree'
      ? 'Search parts, numbers, or descriptions...'
      : activeDashboardTab === 'categories'
        ? 'Search categories...'
        : activeDashboardTab === 'subcategories'
          ? 'Search subcategories...'
          : activeDashboardTab === 'brands'
            ? 'Search brands...'
            : activeDashboardTab === 'models'
              ? 'Search models...'
              : 'Search...';

  const tabPrimaryAction =
    activeDashboardTab === 'tree'
      ? {
          label: 'Add Part',
          onClick: () => {
            setInlinePartData(emptyInlinePartData());
            setIsAddingPartInline(true);
          },
        }
      : activeDashboardTab === 'categories'
        ? { label: 'Add Category', onClick: () => openAddModal('category') }
        : activeDashboardTab === 'subcategories'
          ? { label: 'Add Subcategory', onClick: () => openAddModal('subcategory') }
          : activeDashboardTab === 'brands'
            ? { label: 'Add Brand', onClick: () => openAddModal('brand') }
            : activeDashboardTab === 'models'
              ? { label: 'Add Model', onClick: () => openAddModal('model') }
              : null;

  const showListToolbar =
    activeDashboardTab === 'tree' ||
    activeDashboardTab === 'categories' ||
    activeDashboardTab === 'subcategories' ||
    activeDashboardTab === 'brands' ||
    activeDashboardTab === 'models';

  const clearAllFilters = () => {
    setSearchQuery('');
    setColFilters({
      category: '',
      subcategory: '',
      model: '',
      brand: '',
      partNumber: '',
      msfNumber: '',
      position: '',
      drawing: '',
      dimensions: '',
      description: '',
    });
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <PageHeader
        title="Parts Hierarchy"
        subtitle="Manage catalog parts, categories, brands, and models."
        subtitleVariant="description"
        subtitleAccent={false}
        breadcrumb={[
          { label: 'Admin' },
          { label: 'Parts Hierarchy', current: true },
        ]}
        tabs={
          <div className={`${erpPageTabsClass} overflow-x-auto`}>
            {[
              { id: 'tree', label: 'Catalog', icon: <MdLayers className="h-4 w-4" /> },
              { id: 'categories', label: 'Categories', icon: <MdCategory className="h-4 w-4" /> },
              { id: 'subcategories', label: 'Subcategories', icon: <MdLabel className="h-4 w-4" /> },
              { id: 'brands', label: 'Brands', icon: <MdBusiness className="h-4 w-4" /> },
              { id: 'models', label: 'Models', icon: <MdModelTraining className="h-4 w-4" /> },
              { id: 'partfinder', label: 'PartFinder', icon: <MdStorage className="h-4 w-4" /> },
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveDashboardTab(tab.id as any)}
                aria-current={activeDashboardTab === tab.id ? 'page' : undefined}
                className={`${erpPageTabClasses(activeDashboardTab === tab.id)} inline-flex items-center gap-2 whitespace-nowrap`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setActiveDashboardTab('approvals')}
              aria-current={activeDashboardTab === 'approvals' ? 'page' : undefined}
              className={`${erpPageTabClasses(activeDashboardTab === 'approvals')} inline-flex items-center gap-2 whitespace-nowrap`}
            >
              Pending Approvals
              {(pendingBrandsMeta.total + pendingModelsMeta.total) > 0 && (
                <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-black leading-none text-white">
                  {pendingBrandsMeta.total + pendingModelsMeta.total}
                </span>
              )}
            </button>
          </div>
        }
      />

      <main className="flex flex-col gap-6">
        {showListToolbar && (
          <section aria-label="Search and filters" className={erpSearchToolbarClass}>
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
              <div className={`${erpSearchToolbarFieldClass} min-w-0 flex-1`}>
                <MdSearch className="pointer-events-none" aria-hidden />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  aria-label={searchPlaceholder}
                  className={erpSearchToolbarInputClass}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 xl:shrink-0">
                <ClearFiltersButton
                  visible={!!searchQuery.trim() || (activeDashboardTab === 'tree' && activeColFilterCount > 0)}
                  onClick={clearAllFilters}
                  variant="toolbar"
                />
                {activeDashboardTab === 'tree' && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleDownloadPartsTemplate()}
                      className={erpPageBtnSecondaryClass}
                    >
                      <MdDownload className="h-4 w-4" /> Template
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setBulkUploadResult(null);
                        setIsBulkUploadOpen(true);
                      }}
                      className={erpPageBtnSecondaryClass}
                    >
                      <MdUpload className="h-4 w-4" /> Bulk Upload
                    </button>
                  </>
                )}
                {tabPrimaryAction && (
                  <button
                    type="button"
                    onClick={tabPrimaryAction.onClick}
                    className={erpPageBtnPrimaryClass}
                  >
                    <MdAdd className="h-4 w-4" /> {tabPrimaryAction.label}
                  </button>
                )}
              </div>
            </div>

            {activeDashboardTab === 'tree' && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {CATALOG_COLUMN_FILTERS.map((field) => (
                  <div key={field.key} className={catalogFilterFieldCls}>
                    <label htmlFor={`ph-filter-${field.key}`} className={catalogFilterLabelCls}>
                      {field.label}
                    </label>
                    <input
                      id={`ph-filter-${field.key}`}
                      type="text"
                      placeholder={field.placeholder}
                      value={colFilters[field.key]}
                      className={catalogFilterInputCls}
                      onChange={(e) => setColFilters({ ...colFilters, [field.key]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {activeDashboardTab === 'tree' && (
          <div className="flex flex-col gap-6 animate-in fade-in duration-300">

            {/* Tabular Table representation of the tree */}
            <div className={erpTableShellClass}>
              <div className={`${erpTableScrollClass} max-h-[calc(100vh-22rem)] overflow-y-auto`}>
                <table className={`${erpTableClass} text-left min-w-[1200px]`}>
                  <thead className="sticky top-0 z-20">{catalogTableHead('px-4 py-3.5')}</thead>
                  <tbody>
                    {isAddingPartInline && activeDashboardTab === 'tree' && renderInlinePartAddRow('px-4 py-4')}
                    {catalogPartsLoading ? (
                      <TableBodySkeleton rows={10} cols={11} />
                    ) : catalogParts.length === 0 && !(isAddingPartInline && activeDashboardTab === 'tree') ? (
                      <tr>
                        <td colSpan={11} className="text-center py-16 text-sm text-[hsl(var(--muted-foreground))] font-semibold">
                          <span className="inline-flex flex-col items-center gap-2">
                            <MdOutlineInventory className="h-8 w-8 opacity-40" />
                            No catalog items found
                          </span>
                        </td>
                      </tr>
                    ) : (
                      catalogParts.map((part) => renderCatalogPartRow(part, 'px-4 py-4'))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={catalogMeta.page}
                totalPages={catalogMeta.totalPages}
                onPageChange={setCatalogPage}
                itemsPerPage={LIST_PAGE_SIZE}
                totalItems={catalogMeta.total}
              />
            </div>
          </div>
        )}

        {/* Categories Tab — loaded via GET /admin/categories */}
        {activeDashboardTab === 'categories' && (
          <div className={listStackCls}>
            <div className={listHeadingCardCls}>
              <span className={listPanelTitleCls}>
                <MdCategory className="h-4 w-4 text-[#38bdf8]" />
                Categories ({categoriesMeta.total})
              </span>
            </div>
            <div className={listTableCardCls}>
            <div className={erpTableScrollClass}>
              <table className={`${erpTableClass} text-left`}>
                <thead>
                  <tr className={erpTableHeadRowClass}>
                    <th className={listHeadCellCls}>Name</th>
                    <th className={listHeadCellCls}>Department</th>
                    <th className={listHeadCellCls}>Description</th>
                    <th className={`${listHeadCellCls} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {catalogCategoriesLoading ? (
                    <TableBodySkeleton rows={10} cols={4} />
                  ) : catalogCategories.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={listEmptyCls}>No categories found</td>
                    </tr>
                  ) : (
                    catalogCategories.map((cat) => (
                      <tr key={cat._id} className={`${erpTableRowClasses()} text-sm text-[hsl(var(--foreground))]`}>
                        <td className={`${listCellCls} font-bold text-[hsl(var(--foreground))]`}>{cat.name}</td>
                        <td className={listCellCls}>
                          <span className="inline-flex items-center rounded-full border border-purple-500/30 bg-purple-500/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-purple-300">
                            {cat.department || 'pms'}
                          </span>
                        </td>
                        <td className={`${listCellCls} text-[hsl(var(--muted-foreground))] max-w-md truncate`}>{cat.description || '-'}</td>
                        <td className={`${listCellCls} text-right`}>
                          <div className="flex justify-end gap-1.5">
                            <button type="button" aria-label="Edit category" onClick={(e) => onEditClick(e, 'category', cat)} className={listActionBtnCls}><MdEdit className="w-4 h-4" /></button>
                            <button type="button" aria-label="Delete category" onClick={(e) => onDeleteClick(e, 'category', cat)} className={listDeleteBtnCls}><MdDelete className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={categoriesMeta.page}
              totalPages={categoriesMeta.totalPages}
              onPageChange={setCategoriesPage}
              itemsPerPage={LIST_PAGE_SIZE}
              totalItems={categoriesMeta.total}
            />
            </div>
          </div>
        )}

        {/* Subcategories Tab */}
        {activeDashboardTab === 'subcategories' && (
          <div className={listStackCls}>
            <div className={listHeadingCardCls}>
              <span className={listPanelTitleCls}>
                <MdLabel className="h-4 w-4 text-[#38bdf8]" />
                Subcategories ({subCategoriesMeta.total})
              </span>
            </div>
            <div className={listTableCardCls}>
            <div className={erpTableScrollClass}>
              <table className={`${erpTableClass} text-left`}>
                <thead>
                  <tr className={erpTableHeadRowClass}>
                    <th className={listHeadCellCls}>Name</th>
                    <th className={listHeadCellCls}>Parent Category</th>
                    <th className={listHeadCellCls}>Description</th>
                    <th className={`${listHeadCellCls} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subCategoriesLoading ? (
                    <TableBodySkeleton rows={10} cols={4} />
                  ) : tabSubCategories.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={listEmptyCls}>No subcategories found</td>
                    </tr>
                  ) : (
                    tabSubCategories.map(sub => (
                      <tr key={sub._id} className={`${erpTableRowClasses()} text-sm text-[hsl(var(--foreground))]`}>
                        <td className={`${listCellCls} font-bold text-[hsl(var(--foreground))]`}>{sub.name}</td>
                        <td className={`${listCellCls} text-[hsl(var(--muted-foreground))] font-medium`}>{sub.parentCategoryName || '-'}</td>
                        <td className={`${listCellCls} text-[hsl(var(--muted-foreground))] max-w-md truncate`}>{sub.description || '-'}</td>
                        <td className={`${listCellCls} text-right`}>
                          <div className="flex justify-end gap-1.5">
                            <button type="button" aria-label="Edit subcategory" onClick={(e) => onEditClick(e, 'subcategory', sub, sub.parentCategoryId, 'category')} className={listActionBtnCls}><MdEdit className="w-4 h-4" /></button>
                            <button type="button" aria-label="Delete subcategory" onClick={(e) => onDeleteClick(e, 'subcategory', sub)} className={listDeleteBtnCls}><MdDelete className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={subCategoriesMeta.page}
              totalPages={subCategoriesMeta.totalPages}
              onPageChange={setSubCategoriesPage}
              itemsPerPage={LIST_PAGE_SIZE}
              totalItems={subCategoriesMeta.total}
            />
            </div>
          </div>
        )}

        {/* Brands Tab */}
        {activeDashboardTab === 'brands' && (
          <div className={listStackCls}>
            <div className={listHeadingCardCls}>
              <span className={listPanelTitleCls}>
                <MdBusiness className="h-4 w-4 text-[#38bdf8]" />
                Brands ({brandsMeta.total})
              </span>
            </div>
            <div className={listTableCardCls}>
            <div className={erpTableScrollClass}>
              <table className={`${erpTableClass} text-left`}>
                <thead>
                  <tr className={erpTableHeadRowClass}>
                    <th className={listHeadCellCls}>Name</th>
                    <th className={listHeadCellCls}>Parent Subcategory</th>
                    <th className={listHeadCellCls}>Parent Category</th>
                    <th className={listHeadCellCls}>Description</th>
                    <th className={`${listHeadCellCls} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {brandsLoading ? (
                    <TableBodySkeleton rows={10} cols={5} />
                  ) : tabBrands.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={listEmptyCls}>No brands found</td>
                    </tr>
                  ) : (
                    tabBrands.map(brand => (
                      <tr key={brand._id} className={`${erpTableRowClasses()} text-sm text-[hsl(var(--foreground))]`}>
                        <td className={`${listCellCls} font-bold text-[hsl(var(--foreground))]`}>{brand.name}</td>
                        <td className={`${listCellCls} text-[hsl(var(--muted-foreground))] font-medium`}>{brand.parentSubCategoryName || <span className="text-xs italic text-[#38bdf8]/60">Direct to Category</span>}</td>
                        <td className={`${listCellCls} text-[hsl(var(--muted-foreground))] font-medium`}>{brand.parentCategoryName || '-'}</td>
                        <td className={`${listCellCls} text-[hsl(var(--muted-foreground))] max-w-md truncate`}>{brand.description || '-'}</td>
                        <td className={`${listCellCls} text-right`}>
                          <div className="flex justify-end gap-1.5">
                            <button type="button" aria-label="Edit brand" onClick={(e) => onEditClick(e, 'brand', brand, brand.parentSubCategoryId || brand.parentCategoryId, brand.parentSubCategoryId ? 'subcategory' : 'category')} className={listActionBtnCls}><MdEdit className="w-4 h-4" /></button>
                            <button type="button" aria-label="Delete brand" onClick={(e) => onDeleteClick(e, 'brand', brand)} className={listDeleteBtnCls}><MdDelete className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={brandsMeta.page}
              totalPages={brandsMeta.totalPages}
              onPageChange={setBrandsPage}
              itemsPerPage={LIST_PAGE_SIZE}
              totalItems={brandsMeta.total}
            />
            </div>
          </div>
        )}

        {/* Models Tab */}
        {activeDashboardTab === 'models' && (
          <div className={listStackCls}>
            <div className={listHeadingCardCls}>
              <span className={listPanelTitleCls}>
                <MdModelTraining className="h-4 w-4 text-[#38bdf8]" />
                Models ({modelsMeta.total})
              </span>
            </div>
            <div className={listTableCardCls}>
            <div className={erpTableScrollClass}>
              <table className={`${erpTableClass} text-left`}>
                <thead>
                  <tr className={erpTableHeadRowClass}>
                    <th className={listHeadCellCls}>Name</th>
                    <th className={listHeadCellCls}>Parent Brand</th>
                    <th className={listHeadCellCls}>Description</th>
                    <th className={`${listHeadCellCls} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {modelsTabLoading ? (
                    <TableBodySkeleton rows={10} cols={4} />
                  ) : tabModels.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={listEmptyCls}>No models found</td>
                    </tr>
                  ) : (
                    tabModels.map(model => (
                      <tr key={model._id} className={`${erpTableRowClasses()} text-sm text-[hsl(var(--foreground))]`}>
                        <td className={`${listCellCls} font-bold text-[hsl(var(--foreground))]`}>{model.name}</td>
                        <td className={`${listCellCls} text-[hsl(var(--muted-foreground))] font-medium`}>{model.parentBrandName || '-'}</td>
                        <td className={`${listCellCls} text-[hsl(var(--muted-foreground))] max-w-md truncate`}>{model.description || '-'}</td>
                        <td className={`${listCellCls} text-right`}>
                          <div className="flex justify-end gap-1.5">
                            <button type="button" aria-label="Edit model" onClick={(e) => onEditClick(e, 'model', model, model.parentBrandId, 'brand')} className={listActionBtnCls}><MdEdit className="w-4 h-4" /></button>
                            <button type="button" aria-label="Delete model" onClick={(e) => onDeleteClick(e, 'model', model)} className={listDeleteBtnCls}><MdDelete className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={modelsMeta.page}
              totalPages={modelsMeta.totalPages}
              onPageChange={setModelsPage}
              itemsPerPage={LIST_PAGE_SIZE}
              totalItems={modelsMeta.total}
            />
            </div>
          </div>
        )}

        {activeDashboardTab === 'partfinder' && (
          <PartFinderRegistry onRefreshHierarchy={fetchHierarchy} />
        )}

        {/* Approvals Tab */}
        {activeDashboardTab === 'approvals' && (
          <div className="flex flex-col gap-6 animate-in fade-in duration-300">
            {/* Pending Brands Table */}
            <div className={listStackCls}>
              <div className={listHeadingCardCls}>
                <span className={listPanelTitleCls}>
                  <MdBusiness className="h-4 w-4 text-[#38bdf8]" />
                  Brands Pending Approval ({pendingBrandsMeta.total})
                </span>
              </div>
              <div className={listTableCardCls}>
              <div className={erpTableScrollClass}>
                <table className={`${erpTableClass} text-left`}>
                  <thead>
                    <tr className={erpTableHeadRowClass}>
                      <th className={listHeadCellCls}>Brand Name</th>
                      <th className={listHeadCellCls}>Category</th>
                      <th className={listHeadCellCls}>Description</th>
                      <th className={`${listHeadCellCls} text-right`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvalsLoading ? (
                      <TableBodySkeleton rows={10} cols={4} />
                    ) : pendingBrands.length === 0 ? (
                      <tr>
                        <td colSpan={4} className={listEmptyCls}>No brands pending approval</td>
                      </tr>
                    ) : (
                      pendingBrands.map(brand => (
                        <tr key={brand._id} className={`${erpTableRowClasses()} text-sm text-[hsl(var(--foreground))]`}>
                          <td className={`${listCellCls} font-bold text-[hsl(var(--foreground))]`}>{brand.name}</td>
                          <td className={`${listCellCls} text-[hsl(var(--muted-foreground))] font-medium`}>
                            {brand.categoryId?.name || brand.categoryId || '-'}
                          </td>
                          <td className={`${listCellCls} text-[hsl(var(--muted-foreground))] max-w-md truncate`}>{brand.description || '-'}</td>
                          <td className={`${listCellCls} text-right`}>
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => handleApproveBrand(brand._id)}
                                className={approveBtnCls}
                              >
                                <MdCheck className="w-4 h-4" /> Approve
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRejectBrand(brand._id)}
                                className={rejectBtnCls}
                              >
                                <MdClose className="w-4 h-4" /> Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={pendingBrandsMeta.page}
                totalPages={pendingBrandsMeta.totalPages}
                onPageChange={setPendingBrandsPage}
                itemsPerPage={LIST_PAGE_SIZE}
                totalItems={pendingBrandsMeta.total}
              />
              </div>
            </div>

            {/* Pending Models Table */}
            <div className={listStackCls}>
              <div className={listHeadingCardCls}>
                <span className={listPanelTitleCls}>
                  <MdModelTraining className="h-4 w-4 text-[#38bdf8]" />
                  Models Pending Approval ({pendingModelsMeta.total})
                </span>
              </div>
              <div className={listTableCardCls}>
              <div className={erpTableScrollClass}>
                <table className={`${erpTableClass} text-left`}>
                  <thead>
                    <tr className={erpTableHeadRowClass}>
                      <th className={listHeadCellCls}>Model Name</th>
                      <th className={listHeadCellCls}>Brand</th>
                      <th className={listHeadCellCls}>Description</th>
                      <th className={`${listHeadCellCls} text-right`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvalsLoading ? (
                      <TableBodySkeleton rows={10} cols={4} />
                    ) : pendingModels.length === 0 ? (
                      <tr>
                        <td colSpan={4} className={listEmptyCls}>No models pending approval</td>
                      </tr>
                    ) : (
                      pendingModels.map(model => (
                        <tr key={model._id} className={`${erpTableRowClasses()} text-sm text-[hsl(var(--foreground))]`}>
                          <td className={`${listCellCls} font-bold text-[hsl(var(--foreground))]`}>{model.name}</td>
                          <td className={`${listCellCls} text-[hsl(var(--muted-foreground))] font-medium`}>
                            {model.brandId?.name || model.brandId || '-'}
                          </td>
                          <td className={`${listCellCls} text-[hsl(var(--muted-foreground))] max-w-md truncate`}>{model.description || '-'}</td>
                          <td className={`${listCellCls} text-right`}>
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => handleApproveModel(model._id)}
                                className={approveBtnCls}
                              >
                                <MdCheck className="w-4 h-4" /> Approve
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRejectModel(model._id)}
                                className={rejectBtnCls}
                              >
                                <MdClose className="w-4 h-4" /> Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={pendingModelsMeta.page}
                totalPages={pendingModelsMeta.totalPages}
                onPageChange={setPendingModelsPage}
                itemsPerPage={LIST_PAGE_SIZE}
                totalItems={pendingModelsMeta.total}
              />
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ── Modal Component ── */}
      {(editItem || isAddModalOpen) && (() => {
        const isPartModal = (editItem?.type || addType) === 'part';
        const labelCls = isPartModal
          ? 'text-[10px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wide block mb-1'
          : 'text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-widest block mb-2';
        const inputCls = isPartModal
          ? 'w-full h-8 px-2.5 text-xs bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-md outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all font-medium text-[hsl(var(--foreground))]'
          : 'w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-xl outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all text-sm font-medium text-[hsl(var(--foreground))]';
        const mediaBtnCls = isPartModal
          ? 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold rounded-md border border-[#0088cc]/30 text-[#0088cc] hover:bg-[#0088cc]/10 disabled:opacity-50'
          : 'inline-flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-lg border border-[#0088cc]/30 text-[#0088cc] hover:bg-[#0088cc]/10 disabled:opacity-50';
        return (
        <Modal
          isOpen={true}
          onClose={() => { setEditItem(null); setIsAddModalOpen(false); }}
          title={`${editItem ? 'Edit' : 'Create'} ${editItem?.type || addType}`}
          size={isPartModal ? 'compact' : 'medium'}
          compact={isPartModal}
          noScroll={isPartModal}
        >
          <div className={isPartModal ? 'space-y-3.5' : 'space-y-6 py-2'}>
            <div className={isPartModal ? 'grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3.5' : 'grid grid-cols-1 md:grid-cols-2 gap-5'}>
              {(addType === 'brand' || editItem?.type === 'brand') && (
                <>
                  <div className="md:col-span-2">
                    <label className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-widest block mb-2">
                      Category <span className="text-red-400">*</span>
                    </label>
                    <select
                      required
                      className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-xl outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all text-sm font-medium text-[hsl(var(--foreground))]"
                      value={formData.parentCategoryId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          parentCategoryId: e.target.value,
                          parentSubCategoryId: '',
                        })
                      }
                    >
                      <option value="">-- Select Category --</option>
                      {flatData.categories.map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-widest block mb-2">
                      Sub category <span className="text-red-400">*</span>
                    </label>
                    <select
                      required
                      disabled={!formData.parentCategoryId}
                      className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-xl outline-none disabled:opacity-50 focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all text-sm font-medium text-[hsl(var(--foreground))]"
                      value={formData.parentSubCategoryId}
                      onChange={(e) =>
                        setFormData({ ...formData, parentSubCategoryId: e.target.value })
                      }
                    >
                      <option value="">-- Select Sub category --</option>
                      <option value={BRAND_NO_SUB_CATEGORY}>No sub category</option>
                      {brandFormSubCategoryOptions.map((sub: { _id: string; name: string }) => (
                        <option key={sub._id} value={sub._id}>
                          {sub.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                      Choose <strong>No sub category</strong> when the brand belongs directly under the category.
                    </p>
                  </div>
                </>
              )}

              {!editItem && !addParentId && (
                <>
                  {addType === 'subcategory' && (
                    <div className="md:col-span-2">
                      <label className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-widest block mb-2">Parent Category</label>
                      <select
                        className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-xl outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all text-sm font-medium text-[hsl(var(--foreground))]"
                        value={formData.parentCategoryId}
                        onChange={e => setFormData({ ...formData, parentCategoryId: e.target.value })}
                      >
                        <option value="">-- Select Category --</option>
                        {flatData.categories.map(cat => (
                          <option key={cat._id} value={cat._id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {addType === 'model' && (
                    <div className="md:col-span-2">
                      <label className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-widest block mb-2">Parent Brand</label>
                      <select
                        className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-xl outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all text-sm font-medium text-[hsl(var(--foreground))]"
                        value={formData.parentBrandId}
                        onChange={e => setFormData({ ...formData, parentBrandId: e.target.value })}
                      >
                        <option value="">-- Select Brand --</option>
                        {flatData.brands.map(brand => (
                          <option key={brand._id} value={brand._id}>{brand.name} (Category: {brand.parentCategoryName})</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {addType === 'part' && (
                    <div className="md:col-span-2">
                      <label className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-widest block mb-2">Link To Parent Type</label>
                      <div className="flex gap-4 mb-3">
                        <label className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--foreground))]">
                          <input
                            type="radio"
                            name="partParentType"
                            value="model"
                            checked={formData.targetParentType === 'model'}
                            onChange={() => setFormData({ ...formData, targetParentType: 'model' })}
                          />
                          Model
                        </label>
                        <label className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--foreground))]">
                          <input
                            type="radio"
                            name="partParentType"
                            value="category"
                            checked={formData.targetParentType === 'category'}
                            onChange={() => setFormData({ ...formData, targetParentType: 'category' })}
                          />
                          Category
                        </label>
                      </div>
                      {formData.targetParentType === 'model' ? (
                        <select
                          className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-xl outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all text-sm font-medium text-[hsl(var(--foreground))]"
                          value={formData.parentModelId}
                          onChange={e => setFormData({ ...formData, parentModelId: e.target.value })}
                        >
                          <option value="">-- Select Model --</option>
                          {modelOptions.map(model => (
                            <option key={model._id} value={model._id}>{model.name} (Brand: {model.parentBrandName})</option>
                          ))}
                        </select>
                      ) : (
                        <select
                          className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-xl outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all text-sm font-medium text-[hsl(var(--foreground))]"
                          value={formData.parentCategoryId}
                          onChange={e => setFormData({ ...formData, parentCategoryId: e.target.value })}
                        >
                          <option value="">-- Select Category --</option>
                          {flatData.categories.map(cat => (
                            <option key={cat._id} value={cat._id}>{cat.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </>
              )}

              {editItem?.type === 'subcategory' && (
                <div className="md:col-span-2">
                  <label className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-widest block mb-2">
                    Parent Category
                  </label>
                  <select
                    className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-xl outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all text-sm font-medium text-[hsl(var(--foreground))]"
                    value={formData.parentCategoryId}
                    onChange={(e) => setFormData({ ...formData, parentCategoryId: e.target.value })}
                  >
                    <option value="">-- Select Category --</option>
                    {flatData.categories.map((cat) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                    Parent category is shown for reference; name and summary are what save on update.
                  </p>
                </div>
              )}

              {editItem?.type === 'model' && (
                <div className="md:col-span-2">
                  <label className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-widest block mb-2">
                    Parent Brand
                  </label>
                  <select
                    className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-xl outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all text-sm font-medium text-[hsl(var(--foreground))]"
                    value={formData.parentBrandId}
                    onChange={(e) => setFormData({ ...formData, parentBrandId: e.target.value })}
                  >
                    <option value="">-- Select Brand --</option>
                    {flatData.brands.map((brand) => (
                      <option key={brand._id} value={brand._id}>
                        {brand.name} (Category: {brand.parentCategoryName})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className={isPartModal ? '' : 'md:col-span-2'}>
                <label className={labelCls}>
                  Primary Name <span className="text-red-400">*</span>
                </label>
                <input
                  required
                  className={inputCls}
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              {(editItem?.type === 'category' || addType === 'category') && (
                <div className="md:col-span-2">
                  <label className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-widest block mb-2">
                    Ship department (PMS grouping)
                  </label>
                  <select
                    className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-xl outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all text-sm font-medium text-[hsl(var(--foreground))]"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  >
                    <option value="engine">Engine Department</option>
                    <option value="deck">Deck Department</option>
                    <option value="electrical">Electrical Department</option>
                    <option value="navigation">Navigation Department</option>
                    <option value="safety">Safety Department</option>
                    <option value="environmental">Environmental Department</option>
                  </select>
                  <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                    Category = PMS System. Assign which department this system belongs to.
                  </p>
                </div>
              )}

              {(editItem?.type === 'part' || addType === 'part') && (
                <>
                  <div className={isPartModal ? '' : 'md:col-span-2'}>
                    <label className={labelCls}>Part Number</label>
                    <input
                      className={inputCls}
                      value={formData.partNumber}
                      onChange={e => setFormData({ ...formData, partNumber: e.target.value })}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className={labelCls}>Specifications &amp; Layout</label>
                    <div className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--muted))]/40">
                      <table className="w-full border-collapse text-left text-[11px]">
                        <thead>
                          <tr className="bg-[hsl(var(--muted))] border-b border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] font-semibold uppercase tracking-wide">
                            <th className="px-2.5 py-1.5 border-r border-[hsl(var(--border))] font-semibold">IMPA</th>
                            <th className="px-2.5 py-1.5 border-r border-[hsl(var(--border))] font-semibold">IMO</th>
                            <th className="px-2.5 py-1.5 border-r border-[hsl(var(--border))] font-semibold">Position</th>
                            <th className="px-2.5 py-1.5 border-r border-[hsl(var(--border))] font-semibold">Drawing</th>
                            <th className="px-2.5 py-1.5 font-semibold">Dimensions</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="bg-[hsl(var(--card))]">
                            <td className="p-1 border-r border-[hsl(var(--border))]">
                              <input
                                type="text"
                                className="w-full px-2 py-1 bg-transparent outline-none font-medium text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/50"
                                placeholder="e.g. 61.24.01"
                                value={formData.msfNumber}
                                onChange={e => setFormData({ ...formData, msfNumber: e.target.value })}
                              />
                            </td>
                            <td className="p-1 border-r border-[hsl(var(--border))]">
                              <input
                                type="text"
                                className="w-full px-2 py-1 bg-transparent outline-none font-medium text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/50"
                                placeholder="IMO / ID"
                                value={formData.imoNumber}
                                onChange={e => setFormData({ ...formData, imoNumber: e.target.value })}
                              />
                            </td>
                            <td className="p-1 border-r border-[hsl(var(--border))]">
                              <input
                                type="text"
                                className="w-full px-2 py-1 bg-transparent outline-none font-medium text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/50"
                                placeholder="POS-001"
                                value={formData.position}
                                onChange={e => setFormData({ ...formData, position: e.target.value })}
                              />
                            </td>
                            <td className="p-1 border-r border-[hsl(var(--border))]">
                              <input
                                type="text"
                                className="w-full px-2 py-1 bg-transparent outline-none font-medium text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/50"
                                placeholder="DWG-001"
                                value={formData.drawing}
                                onChange={e => setFormData({ ...formData, drawing: e.target.value })}
                              />
                            </td>
                            <td className="p-1">
                              <input
                                type="text"
                                className="w-full px-2 py-1 bg-transparent outline-none font-medium text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/50"
                                placeholder="10x20x5 cm"
                                value={formData.dimensions}
                                onChange={e => setFormData({ ...formData, dimensions: e.target.value })}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="md:col-span-2 space-y-4 border border-[hsl(var(--border))] rounded-xl p-4 bg-[hsl(var(--muted))]/30">
                    <div>
                      <label className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-widest block mb-2">
                        Images (blueprints, maps, photos)
                      </label>
                      {editItem?.type === 'part' ? (
                        <>
                          <div className="flex flex-wrap gap-3 mb-3">
                            {partImages.map((img) => (
                              <div
                                key={img._id || img.fileUrl}
                                className="relative w-24 h-24 rounded-lg overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--card))] group"
                              >
                                <img
                                  src={resolveMediaUrl(img.fileUrl)}
                                  alt={img.fileName}
                                  className="w-full h-full object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() => void handleDeletePartImage(img._id)}
                                  className="absolute top-1 right-1 p-1 rounded bg-black/60 text-white opacity-0 group-hover:opacity-100"
                                  title="Remove"
                                >
                                  <MdClose className="w-3.5 h-3.5" />
                                </button>
                                <span className="absolute bottom-0 inset-x-0 text-[9px] font-bold uppercase tracking-wide bg-black/55 text-white px-1 py-0.5 truncate">
                                  {img.imageType || 'photo'}
                                </span>
                              </div>
                            ))}
                          </div>
                          <input
                            ref={partImageInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            multiple
                            className="hidden"
                            onChange={(e) => void handleUploadPartImages(e.target.files)}
                          />
                          <button
                            type="button"
                            disabled={partMediaUploading}
                            onClick={() => partImageInputRef.current?.click()}
                            className={mediaBtnCls}
                          >
                            <MdUpload className="w-4 h-4" />
                            {partMediaUploading ? 'Uploading…' : 'Upload images'}
                          </button>
                        </>
                      ) : (
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">
                          Save the part first, then edit it to upload images to Firebase Storage.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-widest block mb-2">
                        Datasheets &amp; manuals (PDF)
                      </label>
                      {editItem?.type === 'part' ? (
                        <>
                          <ul className="space-y-2 mb-3">
                            {partDocuments.map((doc) => (
                              <li
                                key={doc._id || doc.fileUrl}
                                className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-xs"
                              >
                                <div className="min-w-0">
                                  <a
                                    href={resolveMediaUrl(doc.fileUrl)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-semibold text-[#0088cc] hover:underline truncate block"
                                  >
                                    {doc.fileName}
                                  </a>
                                  <span className="text-[hsl(var(--muted-foreground))] uppercase tracking-wide">
                                    {doc.docType || 'General'}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => void handleDeletePartDocument(doc._id)}
                                  className="p-1.5 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
                                  title="Remove"
                                >
                                  <MdClose className="w-4 h-4" />
                                </button>
                              </li>
                            ))}
                          </ul>
                          <input
                            ref={partDocInputRef}
                            type="file"
                            accept="application/pdf,.pdf"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                              const docType = partDocInputRef.current?.dataset.docType || undefined;
                              if (partDocInputRef.current) delete partDocInputRef.current.dataset.docType;
                              void handleUploadPartDocuments(e.target.files, docType);
                            }}
                          />
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={partMediaUploading}
                              onClick={() => {
                                if (partDocInputRef.current) partDocInputRef.current.dataset.docType = 'Datasheet';
                                partDocInputRef.current?.click();
                              }}
                              className={mediaBtnCls}
                            >
                              <MdUpload className="w-4 h-4" /> Upload datasheet
                            </button>
                            <button
                              type="button"
                              disabled={partMediaUploading}
                              onClick={() => {
                                if (partDocInputRef.current) partDocInputRef.current.dataset.docType = 'Manual';
                                partDocInputRef.current?.click();
                              }}
                              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-lg border border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] disabled:opacity-50"
                            >
                              <MdUpload className="w-4 h-4" /> Upload manual
                            </button>
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">
                          Save the part first, then edit it to upload PDF datasheets and manuals.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="min-w-0">
                    <CurrencyAmountInput
                      compact={isPartModal}
                      label="Unit Price"
                      value={formData.priceUSD}
                      onChange={(v) => setFormData({ ...formData, priceUSD: v })}
                      currencyCode={formData.priceCurrency}
                      onCurrencyCodeChange={(code) =>
                        setFormData({ ...formData, priceCurrency: code })
                      }
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={labelCls}>Stock Inventory</label>
                    <div className="flex items-center gap-2.5">
                      <input
                        type="number"
                        min={0}
                        className={`${inputCls} flex-1`}
                        value={formData.stockQuantity}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            stockQuantity: e.target.value === '' ? 0 : Number(e.target.value),
                          })
                        }
                        placeholder="0"
                      />
                      <span className="shrink-0 text-xs font-medium text-[hsl(var(--muted-foreground))] tabular-nums">
                        {Number(formData.stockQuantity) || 0} units
                      </span>
                    </div>
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <label className={labelCls}>Summary</label>
                <textarea
                  rows={isPartModal ? 2 : 3}
                  className={
                    isPartModal
                      ? 'w-full px-2.5 py-1.5 text-xs bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-md resize-none outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all font-medium text-[hsl(var(--foreground))] leading-relaxed'
                      : 'w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm rounded-xl resize-none outline-none focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 transition-all text-sm font-medium text-[hsl(var(--foreground))]'
                  }
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>

            <div className={isPartModal ? 'flex justify-end gap-2.5 pt-1' : 'flex justify-end gap-3 pt-6'}>
              <button
                type="button"
                onClick={() => { setEditItem(null); setIsAddModalOpen(false); }}
                className={
                  isPartModal
                    ? 'px-3.5 py-1.5 text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors'
                    : 'px-6 py-3 font-bold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors'
                }
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSave()}
                className={
                  isPartModal
                    ? 'px-4 py-1.5 text-xs bg-[#0088cc] text-white rounded-md font-semibold transition-all'
                    : 'px-10 py-3 bg-[#0088cc] text-white rounded-xl font-bold transition-all'
                }
              >
                Confirm Changes
              </button>
            </div>
          </div>
        </Modal>
        );
      })()}

      {isBulkUploadOpen && (
        <Modal
          isOpen
          onClose={() => !bulkUploading && setIsBulkUploadOpen(false)}
          title="Bulk upload catalog parts"
          size="medium"
        >
          <div className="space-y-5 py-2">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Upload an Excel file (.xlsx) with columns:{' '}
              <strong className="text-[hsl(var(--foreground))]">
                Category, Subcategory, Brand, Model, Part Number, Position Number, Drawing Number,
                Dimensions, Price, Stock
              </strong>
              . Use <strong>No sub category</strong> in Subcategory when the brand sits directly under the category.
              Missing categories, subcategories, brands, or models are created automatically during import.
            </p>
            <button
              type="button"
              onClick={() => handleDownloadPartsTemplate()}
              className="inline-flex items-center gap-2 text-sm font-bold text-[#38bdf8] hover:underline"
            >
              <MdDownload className="w-4 h-4" /> Download Excel template
            </button>
            <input
              ref={bulkFileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              disabled={bulkUploading}
              className="block w-full text-sm text-[hsl(var(--foreground))] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#0088cc] file:text-white file:font-bold"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleBulkPartsFile(file);
              }}
            />
            {bulkUploading && (
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Importing parts…</p>
            )}
            {bulkUploadResult && (
              <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-4 text-sm">
                <p className="font-bold text-[hsl(var(--foreground))]">
                  Created: {bulkUploadResult.created} · Failed: {bulkUploadResult.failed}
                </p>
                {bulkUploadResult.errors.length > 0 && (
                  <ul className="mt-2 max-h-40 overflow-y-auto space-y-1 text-[hsl(var(--muted-foreground))]">
                    {bulkUploadResult.errors.slice(0, 20).map((err) => (
                      <li key={`${err.row}-${err.message}`}>
                        Row {err.row}: {err.message}
                      </li>
                    ))}
                    {bulkUploadResult.errors.length > 20 && (
                      <li>…and {bulkUploadResult.errors.length - 20} more</li>
                    )}
                  </ul>
                )}
              </div>
            )}
            <div className="flex justify-end pt-2">
              <button
                type="button"
                disabled={bulkUploading}
                onClick={() => setIsBulkUploadOpen(false)}
                className="px-6 py-2.5 font-bold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Shared Node Component ──

function Node({ item, type, icon, accent, expandedItems, toggleExpand, onAdd, onEdit, onDelete, parentId, parentType, children }: any) {
  const isExpanded = expandedItems.has(item._id);
  const nextType = getNextType(type);

  const colors: any = {
    indigo: "bg-indigo-50 text-[#0088cc] border-indigo-100",
    blue: "bg-blue-50 text-[#0088cc] border-blue-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
  };

  return (
    <div className="relative group/node">
      <div className={`
        flex items-center justify-between p-4 rounded-2xl border transition-all duration-300
        ${isExpanded ? 'bg-[hsl(var(--card))] ring-1 ring-[hsl(var(--border))] border-transparent mb-4' : 'bg-[hsl(var(--card))] border-transparent hover:bg-[hsl(var(--muted))]'}
      `}>
        <div className="flex items-center gap-4 flex-1">
          <button 
            onClick={() => toggleExpand(item._id)}
            className={`p-1.5 rounded-lg transition-all ${isExpanded ? 'rotate-90 bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] shadow-inner' : 'text-[hsl(var(--muted-foreground))] hover:bg-[#0088cc]/15'}`}
          >
            <MdChevronRight className="w-6 h-6" />
          </button>

          <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${colors[accent]}`}>
            {React.cloneElement(icon, { className: "w-5 h-5" })}
          </div>

          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-[17px] font-bold text-[hsl(var(--foreground))]">{item.name}</h4>
              <span className="text-[10px] font-black uppercase tracking-widest text-[#0088cc]/45 bg-[hsl(var(--muted))] px-2 py-0.5 rounded border border-[#0088cc]/15">{type}</span>
            </div>
            {item.description && <p className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-1 mt-0.5">{item.description}</p>}
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover/node:opacity-100 transition-all transform translate-x-2 group-hover/node:translate-x-0">
          {nextType && type !== 'category' && (
            <button 
              onClick={() => onAdd(nextType, item._id, type)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-[#0088cc] bg-indigo-50 rounded-lg hover:bg-indigo-100"
            >
              <MdAdd /> {nextType}
            </button>
          )}
          {type === 'category' && (
            <>
              {nextType && (
                <button 
                  onClick={() => onAdd(nextType, item._id, type)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-[#0088cc] bg-indigo-50 rounded-lg hover:bg-indigo-100"
                >
                  <MdAdd /> {nextType}
                </button>
              )}
              <button 
                onClick={() => onAdd('subcategory', item._id, 'category')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 ml-1.5"
              >
                <MdAdd /> subcategory
              </button>
            </>
          )}
          <div className="w-px h-6 bg-[#0088cc]/15 mx-2" />
          <button onClick={() => onEdit(type, item, parentId, parentType)} className="p-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] rounded-lg"><MdEdit className="w-5 h-5" /></button>
          <button onClick={() => onDelete(type, item._id, item.name)} className="p-2 text-red-300 hover:text-red-600 hover:bg-red-50 rounded-lg"><MdDelete className="w-5 h-5" /></button>
        </div>
      </div>

      {isExpanded && children && (
        <div className="ml-14 pl-10 border-l-2 border-dashed border-[hsl(var(--border))] relative animate-in slide-in-from-top-2 duration-500">
          {children}
          {/* Connector Node */}
          <div className="absolute -left-[2px] top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-gray-200 to-transparent" />
        </div>
      )}
    </div>
  );
}

// ── Part Leaf Component ──

function PartCard({ part, onEdit, onDelete }: any) {
  const { formatAmount } = useCurrency();
  return (
    <div className="flex items-center justify-between p-4 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl hover:border-[#0088cc]/40 transition-all group/part">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-[hsl(var(--muted))] rounded-lg flex items-center justify-center text-[hsl(var(--muted-foreground))] group-hover/part:text-[#0088cc] transition-colors">
          <MdOutlineInventory className="w-5 h-5" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-[hsl(var(--foreground))]">{part.name}</span>
            <span className="text-[10px] font-mono font-black text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))] border px-1.5 rounded uppercase">{part.partNumber}</span>
          </div>
          <div className="flex items-center gap-4 mt-1.5">
            <span className="text-xs font-bold text-[#0088cc] bg-indigo-50 px-2 py-0.5 rounded">{formatAmount(part.priceUSD)}</span>
            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-black uppercase ${part.stockQuantity < 10 ? 'bg-red-50 text-red-600 animate-pulse' : 'bg-emerald-50 text-emerald-600'}`}>
              Inventory: {part.stockQuantity}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover/part:opacity-100 transition-all">
        <button onClick={onEdit} className="p-2 text-[hsl(var(--muted-foreground))] hover:text-[#0088cc] hover:bg-indigo-50 rounded-lg"><MdEdit className="w-5 h-5" /></button>
        <button onClick={onDelete} className="p-2 text-[hsl(var(--muted-foreground))] hover:text-red-600 hover:bg-red-50 rounded-lg"><MdDelete className="w-5 h-5" /></button>
      </div>
    </div>
  );
}

function getNextType(current: string) {
  const flow: any = { category: 'brand', subcategory: 'brand', brand: 'model', model: 'part' };
  return flow[current] || null;
}

